import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { generatedStaticZh } from './static-translations'

export type Language = 'en' | 'zh'

const STORAGE_KEY = 'navoption.language'

const messages = {
  en: {
    'app.title.default': 'NavOption - Options Investing Copilot',
    'brief.kind.earnings': 'earnings',
    'brief.kind.exDiv': 'ex-div',
    'brief.session.mid': 'MID',
    'brief.carryover': 'Carried over: ',
    'brief.closingWatch': 'Afternoon: ',
    'brief.newSinceYesterday': 'New since yesterday',
    'brief.diffFrom': 'Not on the {d} watch list',
    'events.overlay.exDiv': '{sym} ex-dividend',
    'events.overlay.exDivAmount': ' · ${v} per share, last payment',
    'analyze.stage.event_pricing': 'Event & option pricing',
    'analyze.stage.data_unavailable': 'Data unavailable',
    'analyze.linesRead': 'readings',
    'analyze.evidenceGrade': 'Evidence {g}',
    'analyze.notCalculated': 'Not calculated',
    'analyze.eventPricing.title': 'Event & option pricing',
    'analyze.eventPricing.reportDate': 'Report date',
    'analyze.eventPricing.countdown': 'in {n} trading days',
    'analyze.eventPricing.impliedMove': 'Implied move',
    'analyze.eventPricing.atmIv': 'At-the-money IV',
    'analyze.eventPricing.ivRank': 'IV rank',
    'analyze.eventPricing.histMedianMove': 'Past median move',
    'analyze.eventPricing.samples': 'over {n} past reports',
    'analyze.fundamentals.title': 'Fundamentals',
    'analyze.fundamentals.note': 'Filed figures with the period they come from. They describe the company; they do not score it.',
    'analyze.peers.title': 'Peers',
    'analyze.peers.rank': 'rank {r} of {n}',
    'analyze.peers.percentile': '{p}th percentile',
    'analyze.peers.note': 'A position inside the peer set the data provider returned, not a judgement on the company.',
    'analyze.unavailable.title': 'What could not be read',
    'macro.title': 'Macro readings',
    'macro.nextFomc': 'Next Fed decision {d}',
    'macro.source': 'Data: {d} · {s}',
    'macro.source.tv': 'daily close, TradingView',
    'macro.label.wti': 'WTI crude',
    'macro.label.brent': 'Brent crude',
    'macro.label.gold': 'Gold',
    'macro.meaning.gold': 'Spot gold price in US dollars per troy ounce.',
    'macro.meaning.brent': 'Price of the nearest-month ICE Brent crude oil futures contract, the main international oil benchmark.',
    'macro.label.us10y': '10Y yield',
    'macro.unit.bp': 'bp',
    'macro.unit.pt': 'pt',
    'macro.unch': 'Unchanged',
    'macro.prev': 'Prev {v}',
    'macro.vsReading': '{c} vs {d}.',
    'macro.header': '{d} close · change vs prior reading',
    'macro.monthData': '{d} data',
    'macro.asOf': 'As of {d}',
    'macro.source.oil': 'front-month future, USD/bbl · TradingView daily close',
    'macro.source.gold': 'spot, USD/oz · TradingView daily close',
    'macro.rollDay': 'Roll day',
    'macro.vsClose': '{c} vs {d} close.',
    'macro.bpNote': '1 bp = 0.01 percentage point.',
    'macro.label.t10y2y': '10Y−2Y',
    'macro.label.breakeven_10y': '10Y breakeven',
    'macro.label.cpi_yoy': 'CPI YoY',
    'macro.label.core_cpi_yoy': 'Core CPI YoY',
    'macro.label.fed_funds': 'Fed funds rate',
    'macro.label.vix': 'VIX',
    'macro.label.dxy': 'Dollar index',
    'macro.meaning.wti': "Price of the nearest-month WTI crude oil futures contract, the main US oil benchmark. Energy prices pass into inflation and into many companies' costs.",
    'macro.meaning.us10y': 'Market yield on the benchmark 10-year US Treasury note. Mortgage rates and stock valuation models are commonly priced off it.',
    'macro.meaning.t10y2y': 'The 10-year yield minus the 2-year yield. Positive means long-term rates are above short-term ones; below zero the curve is called inverted.',
    'macro.meaning.breakeven_10y': "The 10-year breakeven rate: the nominal 10-year Treasury yield minus the 10-year TIPS yield — the yearly inflation at which the two pay the same. It is the bond market's inflation compensation and includes risk and liquidity premia, so it is not a pure forecast.",
    'macro.meaning.cpi_yoy': 'Consumer prices (CPI-U, all items) against the same month a year earlier. Published monthly, usually in the middle of the following month.',
    'macro.meaning.core_cpi_yoy': 'CPI excluding food and energy, against the same month a year earlier.',
    'macro.meaning.fed_funds': "The effective federal funds rate: the volume-weighted median rate on overnight unsecured loans of reserves between banks, held within the Fed's target range. Many loan and deposit rates follow it.",
    'macro.meaning.vix': 'CBOE Volatility Index: the annualized volatility of the S&P 500 over the next 30 days, implied by S&P 500 option prices. Higher means the market is pricing larger swings.',
    'macro.meaning.dxy': "ICE US Dollar Index: the dollar against a fixed-weight basket of six currencies, about 58% euro. A stronger dollar lowers the dollar value of US companies' overseas sales.",
    'attention.dismiss': 'Not now',
    'attention.inTradingDays': 'in {n} trading days',
    'attention.fullCalendar': 'Full 45-day calendar in Events →',
    'attention.exposure.position': '{n} position',
    'attention.exposure.positions': '{n} positions',
    'attention.exposure.notional': 'notional {v}',
    'attention.exposure.furthest': 'furthest expiry {d}',
    'money.stocks': 'Stocks',
    'money.positions': '{n} positions',
    'money.withQuote': '{n} of {total} with a quote',
    'money.unrealized': 'Unrealized',
    'money.withCost': '{n} of {total} with cost',
    'money.lastSession': 'Last session',
    'money.idleCash': 'Idle cash',
    'money.heldForPuts': 'Held for puts',
    'money.pendingAssignment': 'Pending assignment',
    'money.notInAnyTotal': 'not in any total',
    'money.capitalAtRisk': 'Capital at risk',
    'money.netAccount': 'Net account',
    'money.margin': 'Margin',
    'money.maintReq': 'Maintenance req.',
    'money.excessLiquidity': 'Excess liquidity',
    'nav.search.placeholder': 'Symbol',
    'nav.search.label': 'Go to a symbol page',
    'learn.title': 'Learn',
    'learn.subtitle': 'The in-app course, one article at a time.',
    'learn.loading': 'Loading…',
    'learn.unavailable': 'The course is unavailable right now.',
    'learn.minutes': '{n} min',
    'learn.more': 'More',
    'learn.glossary': 'Full glossary',
    'learn.glossary.note': 'Underlined terms across the rest of the site link back here.',
    'brief.moverReason.earnings': 'earnings',
    'brief.moverReason.uoa': 'unusual flow',
    'brief.col.actualEps': 'Actual EPS',
    'brief.col.surprise': 'Surprise',
    'app.title.scan': 'Scanner - NavOption',
    'app.title.pricing': 'Pricing - NavOption',
    'app.title.account': 'Account - NavOption',
    'aria.footer': 'Footer',
    'aria.language': 'Language',
    'aria.mainNav': 'Main navigation',
    'aria.marketSnapshot': 'Market snapshot',
    'brand.subtitle': 'Options Research',
    'common.backHome': 'Back to homepage',
    'common.free': 'FREE',
    'common.pro': 'PRO',
    'common.signInCreate': 'Sign in / create account',
    'footer.legal': 'Educational screening and research tools only - not financial advice. Data may be delayed or incomplete.',
    'language.en': 'EN',
    'language.zh': '中文',
    'market.afterHours': 'After-Hours',
    'market.closed': 'Closed',
    'market.open': 'Open',
    'market.opensSoon': 'Opens soon',
    'market.preMarket': 'Pre-Market',
    'nav.account': 'Account',
    'nav.analyze': 'Stock analysis',
    'nav.brief': 'Brief',
    'nav.earnings': 'Earnings',
    'nav.learn': 'Learn',
    'nav.login': 'Log in',
    'nav.markets': 'Markets',
    'nav.curriculum': 'Curriculum',
    'aria.navGroup': 'Open menu',
    'nav.news': 'News',
    'nav.notes': 'Field Notes',
    'nav.positions': 'Positions',
    'nav.pricing': 'Pricing',
    'nav.privacy': 'Privacy',
    'nav.research': 'Research',
    'nav.scan': 'Scan',
    'nav.support': 'Support',
    'nav.terms': 'Terms',
    'nav.tools': 'Tools',
    'nav.uoa': 'UOA',
    'nav.watchlist': 'Watchlist',
    'nav.wheelTools': 'Wheel Tools',
    'promo.badge': 'NEW',
    'promo.cta.account': 'Redeem in Account ->',
    'promo.cta.signIn': 'Sign in to redeem ->',
    'promo.text': 'NavOption v2 is live - use code LAUNCH30 for 30 days of Pro on us.',
    'gate.ai.body': 'Stock analysis reviews filed financial statements and is available to signed-in users. Free accounts include analysis runs.',
    'gate.ai.title': 'Sign in to run stock analysis',
    'gate.brief.body': 'Sign in to view your daily market brief — pre-market, midday, and post-close editions with index recap, unusual options activity, earnings, and the day\'s watchlist in one place.',
    'gate.brief.title': 'Sign in to view the daily brief',
    'brief.title.premarket': 'PRE-MARKET BRIEF',
    'brief.title.midday': 'MIDDAY BRIEF',
    'brief.title.postclose': 'POST-CLOSE BRIEF',
    'brief.marketDirection': 'Market direction',
    'brief.dataCutoff': 'Data as of',
    'brief.loading': 'Loading report...',
    'brief.degraded': 'AI narrative is temporarily unavailable — showing raw data below.',
    'brief.executive.title': 'Highlights',
    'brief.executive.observations': 'What stands out',
    'brief.executive.watches': 'What to watch next',
    'brief.executive.evidence': 'View details',
    'brief.executive.condition': 'Check when',
    'brief.executive.recheck': 'Recheck if',
    'brief.executive.observe': 'Where to look',
    'brief.coverage.title': 'Report coverage',
    'brief.coverage.available': 'sections with usable content',
    'brief.coverage.note': 'A missing section has no usable content for this report. The period may have been quiet or the data unavailable; it does not by itself mean a source failed.',
    'brief.coverage.missing': 'No usable content:',
    'brief.coverage.freshness': 'Source dates',
    'brief.coverage.current': 'Report date',
    'brief.coverage.prior': 'Earlier',
    'brief.coverage.unknown': 'Unknown',
    'brief.coverage.unknownDate': 'Date unknown',
    'brief.coverage.currentNote': '“Report date” means the source date matches this report’s trading date. It does not mean live data.',
    'brief.coverage.separator': ', ',
    'brief.section.closeRecap': 'Market overview',
    'brief.section.movers': 'Market movers',
    'brief.section.keyLevels': 'Options positioning',
    'brief.section.optionFlow': 'Options flow',
    'brief.section.earningsRecap': 'Earnings recap',
    'brief.section.tomorrowWatch': 'Next-session watch',
    'brief.section.macroWatch': 'Macro calendar',
    'brief.section.todayEarnings': 'Today’s earnings',
    'brief.section.earningsIvWatch': 'Earnings IV watch',
    'brief.section.uoaMorning': 'Morning options activity',
    'brief.section.middayMarket': 'Midday market',
    'brief.section.other': 'Report detail',
    'brief.col.index': 'Index',
    'brief.col.sector': 'Sector',
    'brief.col.close': 'Close',
    'brief.col.change': 'Change',
    'brief.col.symbol': 'Symbol',
    'brief.col.side': 'Side',
    'brief.col.strike': 'Strike',
    'brief.col.dte': 'DTE',
    'brief.col.notional': 'Premium $',
    'brief.col.multiple': 'Vol ratio',
    'brief.col.price': 'Price',
    'brief.col.premium': 'Premium',
    'brief.col.earningsFlag': 'Earnings',
    'brief.col.estEps': 'Est EPS',
    'brief.col.estRev': 'Est Rev',
    'brief.col.atmIv': 'ATM IV',
    'brief.col.ivChange': 'IV change',
    'brief.col.event': 'Event',
    'brief.col.kind': 'Kind',
    'brief.col.impact': 'Impact',
    'brief.col.when': 'When',
    'brief.col.apy': 'Ann. (snapshot)',
    'brief.col.iv': 'IV',
    'brief.tier.smart_flow': 'Smart flow',
    'brief.tier.contrarian': 'Contrarian',
    'brief.tier.lottery': 'Lottery',
    'brief.tier.bearish_hedge': 'Hedge',
    'brief.session.bmo': 'BMO',
    'brief.session.amc': 'AMC',
    'brief.col.ivPercentile': 'IV percentile (90d)',
    'brief.col.earningsDate': 'Earnings',
    'brief.ivPercentileDisclosure': 'IV percentile describes only where this stock\'s own IV sits within its trailing 90 days — not a directional prediction.',
    'brief.snapshotAsOf': 'Prior-close snapshot',
    'brief.slotAria': 'Report time slot',
    'brief.slot.premarket': 'Pre-Market',
    'brief.slot.midday': 'Midday',
    'brief.slot.postclose': 'Post-Close',
    'brief.positioning.premarket': 'Pre-market overview — today\'s schedule and prior-close context, not trading advice.',
    'brief.positioning.midday': 'Midday snapshot — how the session is unfolding, not trading advice.',
    'brief.positioning.postclose': 'End-of-day market recap — what happened today, not trading advice.',
    'brief.empty.premarket': 'The pre-market brief is generated after 08:30 ET on trading days — check back soon.',
    'brief.empty.midday': 'The midday brief is generated after 12:30 ET on trading days (skipped on early-close days).',
    'brief.empty.postclose': 'Today\'s post-close brief is generated around 17:15 ET — check back then.',
    'brief.emptyGoto': 'Or check another brief:',
    'brief.middayAfterCloseHint': 'The post-close brief is generated around 17:15 ET.',
    'brief.priorTradingDay': 'Prior trading day',
    'brief.checkup': 'Stock analysis',
    'brief.breadth.advancers': 'Up',
    'brief.breadth.decliners': 'Down',
    'brief.breadth.unchanged': 'Flat',
    'brief.breadth.universePrefix': 'Of the ',
    'brief.breadth.universeSuffix': ' option-liquid symbols this platform tracks: ',
    'brief.breadth.notMarketWide': ' (not whole-market breadth)',
    'brief.news.none': 'No matching public headline dated today',
    'brief.news.unavailable': 'Headline data unavailable today',
    'brief.news.error': 'Headline lookup did not complete',
    'brief.movers.help': 'A headline is only something published the same day — it is not a statement that it caused the move.',
    'brief.col.spot': 'Spot',
    'brief.col.putWall': 'Densest put OI',
    'brief.col.callWall': 'Densest call OI',
    'brief.col.topStrikes': 'Densest strikes',
    'brief.col.snapshotTs': 'Snapshot (ET)',
    'brief.keyLevels.disclosurePrefix': 'Computed from the chain snapshot at ',
    'brief.keyLevels.disclosureSuffix': ' ET. Open interest is the previous settlement day\'s figure (T+1). Gross basis — not directly comparable across symbols or across days. It says where positioning sits, not that price will turn here.',
    'brief.flow.verifiedBadge': 'D-1 verified',
    'brief.flow.verifiedLabelPrefix': 'Tick-level block/sweep structure, trade date ',
    'brief.flow.verifiedLabelSuffix': '. Today\'s tick data is only available at T+1.',
    'brief.flow.unusualLabelPrefix': 'Concentrated volume today: ',
    'brief.flow.help': 'A block is one large single print; a sweep is one order taking quotes across several venues at the same instant. Multi-leg package legs are excluded — single-leg prints only.',
    'brief.flow.topPrintPrefix': 'Largest single print ',
    'brief.flow.topPrintSuffix': ' · show prints',
    'brief.unit.contracts': 'contracts',
    // Execution mechanism only. These labels describe how a print reached the
    // tape and must never be worded as who traded or which way.
    'brief.cond.crossNonIso': 'single-leg cross (non-ISO)',
    'brief.cond.auctionNonIso': 'single-leg auction (non-ISO)',
    'brief.cond.floorTrade': 'floor trade',
    'brief.cond.autoExecution': 'automatic execution',
    'brief.cond.intermarketSweep': 'intermarket sweep',
    'brief.col.blocks': 'Blocks',
    'brief.col.sweeps': 'Sweeps',
    'brief.col.blockPremium': 'Block premium',
    'brief.col.sweepPremium': 'Sweep premium',
    'brief.col.cleanVolume': 'Clean volume',
    'brief.col.multiLeg': 'Multi-leg',
    'brief.group.earnings': 'Earnings',
    'brief.group.dividends': 'Going ex-dividend',
    'brief.group.wheelPicks': 'Wheel excerpt',
    'brief.col.exDivDate': 'Ex-date',
    'brief.col.divAmount': 'Dividend',
    'brief.col.divYield': 'Yield',
    'analyze.stop': 'Stop',
    'analyze.stage.stopped': 'Stopped',
    'analyze.perMonth': '/mo',
    'analyze.deepTitle': 'Deep analysis adds insider history, peer comparison, a volatility band, and a plain-language explanation. Uses one of your monthly deep analyses.',
    'analyze.kicker': 'STOCK ANALYSIS',
    'analyze.title': 'Stock Analysis',
    'analyze.intro.pro': 'Review cash flow, debt, revenue, and price volatility. Pro adds insider history, peer comparison, and a volatility band.',
    'analyze.intro.free': 'Review cash flow, debt, revenue, and price volatility. The result identifies risks found and data that could not be checked.',
    'analyze.sourceNote': 'Read from filed financial statements, not options-chain data.',
    'analyze.run': 'Run analysis',
    'analyze.running': 'Running...',
    'analyze.loading': 'Reviewing available financial and market data...',
    'analyze.outcome.vetoed': 'Risk flagged',
    'analyze.outcome.marginal': 'Needs a closer look',
    'analyze.outcome.pass': 'No major issues found',
    'analyze.outcome.unknown': 'Not enough data',
    'analyze.outcome.unavailable': 'Result unavailable',
    'analyze.coverage': 'Data coverage',
    'analyze.checksAvailable': 'checks available',
    'analyze.laterChecks': 'Later checks were not performed after a risk was flagged, so those questions remain open.',
    'analyze.scopeNote': 'This result covers only the checks performed with available data. It does not value the company or recommend buying or selling the stock.',
    'analyze.check.vetoed': 'RISK FLAGGED',
    'analyze.check.marginal': 'CLOSER LOOK',
    'analyze.check.pass': 'WITHIN RANGE',
    'analyze.check.unknown': 'NOT ENOUGH DATA',
    'analyze.notReported': 'not reported',
    'analyze.noScore': 'No score',
    'analyze.log.rawToggle': 'Raw event log',
    'analyze.log.idle': 'Idle.',
    'analyze.stage.mode': 'Starting analysis',
    'analyze.stage.stock': 'Reading market data',
    'analyze.stage.fundamentals': 'Fundamentals read',
    'analyze.stage.news': 'Scanning news',
    'analyze.stage.peer_compare': 'Peer comparison',
    'analyze.stage.insider': 'Insider activity',
    'analyze.stage.done': 'Done',
    'analyze.stage.error': 'Error',
    'analyze.stage.unknown': 'Working...',
    'home.actions.analyze': 'Analyze a stock',
    'home.actions.pricing': 'Compare plans',
    'home.actions.scan': 'Browse income ideas',
    'home.eyebrow': 'The wheel strategy, made simple',
    'home.headline': 'Research wheel trades on stocks you know.',
    'home.body': 'Compare wheel trades across major US stocks, review cash flow, debt, revenue, and price volatility, then track each position from entry to exit. Premium can provide income while you wait, but assignment and market losses remain possible.',
    'auth.apple.login': 'Sign in with Apple',
    'auth.apple.register': 'Sign up with Apple',
    'auth.body.login': 'Sign in with Apple or Google to keep your workspace synced across web and iOS.',
    'auth.body.register': 'One account for web and iOS. Sign up with Apple or Google to save your scanner, watchlist, alerts, and positions.',
    'auth.google.login': 'Sign in with Google',
    'auth.google.register': 'Sign up with Google',
    'auth.loadingApple': 'Sign in with Apple is still loading. Try again in a moment.',
    'auth.loadingGoogle': 'Sign in with Google is still loading. Try again in a moment.',
    'auth.message.appleFailed': 'Apple sign-in failed.',
    'auth.message.appleTokenMissing': 'Apple did not return an identity token.',
    'auth.message.googleCodeMissing': 'Google did not return an authorization code.',
    'auth.message.signingInApple': 'Signing in with Apple...',
    'auth.message.signingInGoogle': 'Signing in with Google...',
    'auth.title.login': 'Sign in',
    'auth.title.register': 'Create account',
    'account.eyebrow': 'Account',
    'account.heading': 'Your NavOption workspace.',
    'account.loading': 'Loading workspace...',
    'account.signingOut': 'Signing out...',
    'notFound.body': 'This page does not exist or has moved.',
    'notFound.title': 'Page not found',
    'nav.events': 'Events',
    'nav.journal': 'Journal',
    'events.kicker': 'EVENTS',
    'events.title': 'Event cycle',
    'events.subtitle': 'Scheduled macro releases for the next 45 days, with your expirations and earnings dates laid over them.',
    'events.next.label': 'Next event',
    'events.next.tradingDays': '{n} trading days away',
    'events.timeline.title': 'Timeline',
    'events.empty': 'No scheduled events in the next 45 days.',
    'events.overlay.expiry': 'Your {sym} option expires',
    'events.overlay.earnings': '{sym} earnings',
    'events.overlay.holding': ' (holding)',
    'events.overlay.position': ' (position)',
    'events.overlay.spans': 'spans {label}',
    'events.overlay.empty': 'Add a position or a holding and your expirations and earnings dates will appear here.',
    'events.readings.title': 'Readings',
    'events.reading.rank20d': 'Percentile {n} of 20-day changes over the past year',
    'events.reading.rank5d': 'Percentile {n} of 5-day changes over the past year',
    'events.reading.rankMonthly': 'Percentile {n} of monthly changes over the past year',
    'events.reading.statMonth': 'Stat month {m}',
    'events.reading.released': 'Released {d}',
    'events.asOf': 'Data date {d}',
    'holdings.title': 'Stocks',
    'holdings.add': '+ Add holding',
    'holdings.cancel': 'Cancel',
    'holdings.save': 'Save',
    'holdings.saving': 'Saving...',
    'holdings.symbol': 'Symbol',
    'holdings.qty': 'Shares',
    'holdings.costBasis': 'Cost per share',
    'holdings.bucket': 'Bucket',
    'holdings.bucket.core': 'Core',
    'holdings.bucket.event': 'Event',
    'holdings.bindEvent': 'Bound event',
    'holdings.noEvent': 'None',
    'holdings.thesis': 'Why you hold it',
    'holdings.optional': 'Optional',
    'holdings.check': 'Analyze',
    'holdings.edit': 'Edit',
    'holdings.delete': 'Remove',
    'holdings.empty': 'No stock holdings yet.',
    'holdings.confirmDelete': 'Remove {sym} from your holdings?',
    'holdings.notSet': 'Not set',
    'holdings.shares': 'shares',
    'holdings.account.title': 'Account',
    'holdings.account.netLiq': 'Net liquidation value',
    'holdings.account.marginDebt': 'Margin debt',
    'holdings.account.reservedCash': 'Reserved cash',
    'holdings.account.maintenancePct': 'Maintenance margin %',
    'holdings.assign.kicker': 'ASSIGNED',
    'holdings.assign.title': 'Record as a stock holding?',
    'holdings.assign.strike': 'strike',
    'holdings.assign.premiums': 'premiums collected',
    'holdings.assign.skip': 'Skip',
    'holdings.callCandidates': 'Call candidates',
    'holdings.callCandidates.hide': 'Hide',
    'holdings.value': 'Value',
    'holdings.row.costBasis': 'cost basis {v}',
    'holdings.row.unrealized': 'unrealized {v} ({pct})',
    'cc.title': 'Covered calls on {sym}',
    'cc.subtitle': '{qty} shares · covers {n} contracts',
    'cc.subtitle.one': '{qty} shares · covers 1 contract',
    'cc.openCalls.dte': '{d}d',
    'cc.openCalls.one': '1 call already open on these shares: {list}',
    'cc.openCalls.many': '{n} calls already open on these shares: {list}',
    'cc.loading': 'Loading candidates...',
    'cc.error': 'Could not load covered-call candidates. Try again in a moment.',
    'cc.costBasis': 'Cost per share {v}',
    'cc.costBasis.missing': 'Cost per share not recorded',
    'cc.assignedAt': 'Assigned at {v}',
    'cc.currentPrice': 'Current price {v}',
    'cc.lastClose': 'Last close {v}',
    'cc.vsCost': 'Vs cost {v}',
    'cc.vsAssigned': 'Vs assigned strike {v}',
    'cc.ref.cost': 'your cost',
    'cc.ref.assigned': 'your assigned strike',
    'cc.depth.spot': 'No cost on file, so the strike floor is spot — a call under spot would already be in the money.',
    'cc.depth.above': '{amt} above {ref} — strikes start at spot.',
    'cc.depth.shallow': '{pct} under {ref}, inside the 5% band the SOP sells calls in.',
    'cc.depth.deep': 'More than 5% under {ref} — calls at or above it often don’t exist, and the SOP notes calls recover only a few percent of a hole this deep.',
    'cc.rule': 'Showing calls {min}–{max} days out, strike ≥ {floor}, open interest ≥ {oi}.',
    'cc.row.strike': '{strike} · {otm} OTM · {date} ({dte}d)',
    'cc.row.premium': '{premium} premium · {apy} APY · Δ{delta} · OI {oi}',
    'cc.row.ifCalled': 'If called: {value} · {pct} vs {ref}',
    'cc.vsRef.cost_basis': 'cost',
    'cc.vsRef.assigned_strike': 'assigned strike',
    'cc.vsRef.spot': 'spot',
    'cc.empty': 'No call meets the rule above. The SOP says check again in 5 trading days.',
    'cc.empty.noChain': 'We don’t carry an option chain for {sym}.',
    'cc.footer': 'Not an order. Sorted by premium, which puts strikes closest to spot on top — the ones most likely to be called away.',
    'regime.title': 'Market state',
    'regime.state.recovery': 'Recovery',
    'regime.state.stress_up': 'Stressed uptrend',
    'regime.state.calm_up': 'Calm uptrend',
    'regime.state.bear': 'Bear',
    'regime.state.correction': 'Correction',
    'regime.asOf': 'as of {d} close',
    'regime.above': 'SPY above its 200-day average',
    'regime.below': 'SPY below its 200-day average',
    'regime.dd': '{v}% below its peak close',
    'regime.rv': '21-day realized vol {v}%',
    'regime.sop': 'Wheel SOP for this state:',
    'regime.sop.recovery': 'regular cadence — strikes more than 15% OTM, 7–14 DTE, no leverage to reach for yield.',
    'regime.sop.stress_up': 'regular cadence — strikes more than 15% OTM, 7–14 DTE; a fresh realized-vol cross above 25% starts the risk check.',
    'regime.sop.calm_up': 'regular cadence — strikes more than 15% OTM, 7–14 DTE, no leverage to reach for yield.',
    'regime.sop.bear': 'strikes may sit 0–5% OTM, still 7–14 DTE — only if you kept cash in reserve.',
    'regime.sop.correction': 'keep the regular cadence — the one out-of-sample episode reversed the sign of the in-sample rule.',
    'regime.sop.source': 'From the Wheel SOP',
    'scan.cash': 'Cash req.',
    'scan.cash.shares': 'shares',
    'scan.maxCash': 'Max cash / contract',
    'scan.maxCash.putsOnly': 'puts only',
    'scan.maxCash.note': 'Strike × 100, before the premium received.',
    'attention.source': 'from your watchlist, holdings & positions',
    'attention.holding.earnings': 'You hold {sym} — earnings {when}',
    'attention.impliedMove': 'options imply ±{v}%',
    'attention.when.today': 'today',
    'attention.when.inDays': 'in {d}d',
    'attention.new': 'NEW',
    'attention.density.share': '{n} share',
    'attention.density.shares': '{n} shares',
    'attention.density.contract': '{n} contract',
    'attention.density.contracts': '{n} contracts',
    'attention.density.vsStrike': '{v}% vs strike',
    'attention.rv21': 'RV21 {v}%, previously {p}%',
    'attention.volCheck.open': 'Risk check',
    'attention.today': 'Today · {d}',
    'attention.verdict.breach': 'An account limit is breached — reduce risk first',
    'attention.verdict.items1': '1 thing needs you today',
    'attention.verdict.items': '{n} things need you today',
    'attention.verdict.clear': 'No rule asks you to act today',
    'attention.verdict.clearSub': 'Not trading is a valid choice.',
    'attention.tile.nextEvent': 'Next major event',
    'attention.tile.noEvent': 'None in the next 14 days',
    'attention.tile.spans1': '1 of your positions spans it',
    'attention.tile.spans': '{n} of your positions span it',
    'attention.tile.scenario': 'Market scenario',
    'attention.tile.account': 'Account risk',
    'attention.tile.stress': 'Stress loss {v} of {b} budget',
    'attention.tile.ledgerOff': 'Not set up',
    'attention.tile.ledgerOffSub': 'Set a loss budget to see exposure and stress here →',
    'attention.breach.next': 'Reduce per your plan →',
    'attention.scenario.means.A': 'Event trades may be considered; margin stays off by default',
    'attention.scenario.means.B': 'No new event trades; strategy margin down to 0',
    'attention.scenario.means.C': 'Strategy paused; exit event positions per plan',
    'attention.tile.exposure': 'Exposure {v}',
    'attention.tile.margin': 'Margin {v}',
    'attention.tile.elHint': 'Exposure = stock value ÷ net liquidation (E). Margin = borrowed ÷ net liquidation (L).',
    'attention.col.next': 'Coming up',
    'attention.flow': 'Unusual flow on your symbols · {n}',
    'attention.flow.baseline': '{v}× baseline',
    'attention.flow.held': 'HELD',
    'attention.flow.watched': 'WATCHED',
    'positions.markAssigned': 'Assigned',
    'positions.confirmAssigned': 'Mark this {sym} {side} as assigned?',
    'positions.markExercised': 'Exercised',
    'positions.confirmExercised': 'Mark this {sym} long call as exercised?',
    'positions.side.csp': 'Cash-Secured Put',
    'positions.side.cc': 'Covered Call',
    'positions.side.lc': 'Long Call',
    'positions.side.lcNote': 'For a long call, premium is what you paid per share.',
    'positions.atRisk': '{v} at risk',
    'symbol.previousClose': 'Previous close',
    'symbol.fiveDayMove': '5D move',
    'symbol.volume': 'Volume',
    'symbol.range52w': '52W range',
    'symbol.avgChainIv': 'Avg chain IV',
    'symbol.bestApy': 'Best APY',
    'symbol.dividendYield': 'Dividend yield',
    'journal.kicker': 'JOURNAL',
    'journal.title': 'Decision journal',
    'journal.subtitle': 'What you did, why you did it, and what the symbol did afterwards.',
    'journal.kind': 'Type',
    'journal.kind.option': 'Option',
    'journal.kind.stock': 'Stock',
    'journal.kind.noTrade': 'No trade',
    'journal.position': 'Position',
    'journal.choosePosition': 'Choose an open position',
    'journal.holding': 'Holding',
    'journal.chooseHolding': 'Choose a holding',
    'journal.symbol': 'Symbol',
    'journal.action': 'Action',
    'journal.bindEvent': 'Bound event',
    'journal.noEvent': 'None',
    'journal.eventEarnings': 'earnings',
    'journal.reason': 'Reason',
    'journal.reason.placeholder': 'Why you made this call',
    'journal.save': 'Save entry',
    'journal.saving': 'Saving...',
    'journal.delete': 'Remove',
    'journal.empty': 'No entries yet.',
    'journal.quick.placeholder': 'Why you are sitting today out',
    'journal.quick.button': 'Decided not to trade today',
    'journal.action.open': 'Open',
    'journal.action.close': 'Close',
    'journal.action.roll': 'Roll',
    'journal.action.assign': 'Assigned',
    'journal.action.buy': 'Buy',
    'journal.action.sell': 'Sell',
    'journal.action.add': 'Add',
    'journal.action.reduce': 'Reduce',
    'journal.action.hold': 'Hold',
    'journal.snap.premium': 'premium',
    'journal.snap.ivRank': 'IV rank',
    'journal.echo.bought': 'After buying, {sym} moved {pct} over 10 trading days',
    'journal.echo.sold': 'After selling, {sym} moved {pct} over 10 trading days',
    'journal.echo.noTrade': '{sym} moved {pct} over 10 trading days',
    'journal.echo.unavailable': 'No close data, not settled',
    'nav.ledger': 'Risk ledger',
    'journal.logDecision': 'Log a decision',
    'ledger.kicker': 'LEDGER',
    'ledger.title': 'Event-bucket risk ledger',
    'ledger.subtitle': 'The limits and framework conditions you wrote down, checked against the data for today.',
    'ledger.off.body': 'For investors who run an event bucket with ordinary stocks or non-leveraged ETFs. Option positions are not included.',
    'ledger.off.enable': 'Turn the ledger on',
    'ledger.tab.today': 'Today',
    'ledger.tab.settings': 'Settings',
    'ledger.tab.calc': 'Calculators',
    'ledger.tab.review': 'Review',
    'ledger.save': 'Save settings',
    'ledger.saving': 'Saving...',
    'ledger.choose': 'Choose',
    'ledger.missing.title': 'The status cards stay read-only until these are written down:',
    'ledger.readonly': 'Status is read-only until every setting is filled in.',
    'ledger.step1': 'Step 1 - Account limits and budget',
    'ledger.step2': 'Step 2 - Bucket split and concentration',
    'ledger.step3': 'Step 3 - Framework conditions and invalidation',
    'ledger.margin.title': 'Margin (off by default)',
    'ledger.margin.enable': 'I borrow on margin in this account',
    'ledger.margin.help': 'Leave this off unless you actually borrow. With it off, margin ratio L is still read from your account snapshot, and no margin limit is checked.',
    'ledger.stress.title': 'Stress scenarios',
    'ledger.stress.help': 'Name each index decline you want checked every day and how far the index falls in it. Stress loss is beta-weighted market value times that decline.',
    'ledger.stress.name': 'Scenario name',
    'ledger.stress.drop': 'Index decline %',
    'ledger.stress.add': '+ Add scenario',
    'ledger.stress.remove': 'Remove',
    'ledger.metric.coreCpi': 'Core CPI MoM',
    'ledger.metric.cpi': 'CPI MoM',
    'ledger.consecutive.one': 'One release',
    'ledger.consecutive.two': 'Two consecutive releases',
    'ledger.energy.metricNote': 'The energy condition compares WTI with its average over the latest published CPI stat month.',
    'ledger.f.account.loss_budget_pct': 'Loss budget (% of net liq)',
    'ledger.h.account.loss_budget_pct': 'How to set this: the loss in one stress scenario you could absorb without changing anything else in your life. Write it as a share of net liquidation value, not a dollar amount.',
    'ledger.f.account.drawdown_warn_pct': 'Drawdown warning level (%)',
    'ledger.h.account.drawdown_warn_pct': 'How to set this: the fall from your equity high-water mark at which you want the account flagged while you are still comfortable.',
    'ledger.f.account.drawdown_pause_pct': 'Drawdown pause level (%)',
    'ledger.h.account.drawdown_pause_pct': 'How to set this: the fall from the high-water mark past which you had already decided to stop opening new event-bucket exposure.',
    'ledger.f.account.equity_high_water': 'Equity high-water mark',
    'ledger.h.account.equity_high_water': 'How to set this: the highest net liquidation value this account has reached, as your own records show it. Drawdown is measured against it.',
    'ledger.f.account.exposure_cap_e': 'Exposure multiple E cap',
    'ledger.h.account.exposure_cap_e': 'How to set this: the highest ratio of total stock market value to net liquidation value you are willing to carry.',
    'ledger.f.account.single_name_cap_pct': 'Single-name cap (%)',
    'ledger.h.account.single_name_cap_pct': 'How to set this: the largest share of net liquidation value you allow any one symbol to reach.',
    'ledger.f.account.sector_cap_pct': 'Sector cap (%)',
    'ledger.h.account.sector_cap_pct': 'How to set this: the largest share of net liquidation value you allow any one sector to reach.',
    'ledger.f.margin.l_cap': 'Margin ratio L cap',
    'ledger.h.margin.l_cap': 'How to set this: the highest ratio of margin debt to net liquidation value you are willing to carry.',
    'ledger.f.margin.cost_pct': 'Borrowing cost (% per year)',
    'ledger.h.margin.cost_pct': 'How to set this: the annual rate your broker charges on the debit balance, taken from your statement.',
    'ledger.f.margin.maintenance_buffer_pct': 'Maintenance buffer (%)',
    'ledger.h.margin.maintenance_buffer_pct': 'How to set this: how much room above the maintenance requirement your broker sets you want to keep before you would act.',
    'ledger.f.margin.profit_trigger_pct': 'Profit trigger (%)',
    'ledger.h.margin.profit_trigger_pct': 'How to set this: the gain on the event bucket at which your own written rule says to revisit the margin level.',
    'ledger.f.margin.post_profit_l_target': 'Margin ratio L after the trigger',
    'ledger.h.margin.post_profit_l_target': 'How to set this: the L you wrote down as the one to hold once the profit trigger is reached.',
    'ledger.f.margin.post_profit_loss_budget_pct': 'Loss budget after the trigger (%)',
    'ledger.h.margin.post_profit_loss_budget_pct': 'How to set this: the loss budget that replaces the first one once the profit trigger is reached.',
    'ledger.f.framework.inflation.metric': 'Inflation metric',
    'ledger.h.framework.inflation.metric': 'How to set this: pick the monthly print you actually follow. The ledger reads it from the macro series, never from a forecast.',
    'ledger.f.framework.inflation.pass_max': 'Inflation pass at or below (%)',
    'ledger.h.framework.inflation.pass_max': 'How to set this: the monthly reading at or under which you count the inflation condition as met.',
    'ledger.f.framework.inflation.fail_above': 'Inflation fail above (%)',
    'ledger.h.framework.inflation.fail_above': 'How to set this: the reading above which you had already decided the inflation leg of your framework no longer holds.',
    'ledger.f.framework.inflation.consecutive': 'Releases required',
    'ledger.h.framework.inflation.consecutive': 'How to set this: whether one print is enough for you, or you only count two in a row.',
    'ledger.f.framework.energy.pass_max': 'Energy pass at or below (%)',
    'ledger.h.framework.energy.pass_max': 'How to set this: the WTI change against the CPI stat month average at or under which you count the energy condition as met.',
    'ledger.f.framework.energy.warn_above': 'Energy warn above (%)',
    'ledger.h.framework.energy.warn_above': 'How to set this: the change above which you want the condition flagged but not yet called failed.',
    'ledger.f.framework.energy.fail_above': 'Energy fail above (%)',
    'ledger.h.framework.energy.fail_above': 'How to set this: the change above which you had already decided the energy leg no longer holds.',
    'ledger.f.framework.policy.target_meeting': 'Target FOMC meeting',
    'ledger.h.framework.policy.target_meeting': 'How to set this: the meeting your policy condition is about. A FedWatch entry has to name the same meeting to be comparable with the last one.',
    'ledger.f.framework.policy.pass_max_net_hikes': 'Policy pass at or below net hikes',
    'ledger.h.framework.policy.pass_max_net_hikes': 'How to set this: the net number of hikes priced to that meeting at or under which you count the policy condition as met.',
    'ledger.f.framework.policy.fail_above_net_hikes': 'Policy fail above net hikes',
    'ledger.h.framework.policy.fail_above_net_hikes': 'How to set this: the net number above which you had already decided the policy leg no longer holds.',
    'ledger.f.framework.policy.reading_ttl_days': 'FedWatch entry validity (days)',
    'ledger.h.framework.policy.reading_ttl_days': 'How to set this: how long one hand-entered FedWatch reading stays usable before the policy condition goes back to unknown.',
    'ledger.f.framework.rates.warn_5d_bp': '10Y five-day change warning (bp)',
    'ledger.h.framework.rates.warn_5d_bp': 'How to set this: the five-day move in the 10-year yield, in basis points, that you want flagged.',
    'ledger.f.framework.rates.fail_no_retrace_days': 'Trading days without retrace before fail',
    'ledger.h.framework.rates.fail_no_retrace_days': 'How to set this: how many trading days the yield may stay past your warning level without retracing before you call the rates leg failed.',
    'ledger.f.framework.rates.retrace_target_pct': 'Retrace target (%)',
    'ledger.h.framework.rates.retrace_target_pct': 'How to set this: how much of the move has to come back before you count it as retraced.',
    'ledger.f.framework.earnings.universe': 'Earnings check universe',
    'ledger.h.framework.earnings.universe': 'How to set this: the symbols whose Analyze gates you want run for the earnings condition. Separate them with commas.',
    'ledger.today.accountCard': 'Account hard limits',
    'ledger.today.frameworkCard': 'Market framework',
    'ledger.today.e': 'Exposure multiple E',
    'ledger.today.l': 'Margin ratio L',
    'ledger.today.n': 'Net liquidation value',
    'ledger.today.drawdown': 'Drawdown',
    'ledger.today.marginCall': 'Decline to a maintenance call',
    'ledger.today.bucketCore': 'Core bucket',
    'ledger.today.bucketEvent': 'Event bucket',
    'ledger.today.longCall': 'Long option premium paid {v} · {n} contracts',
    'ledger.today.longCallNote': 'This is the most those contracts can lose. It is not counted in E.',
    'ledger.today.stress': 'Stress loss',
    'ledger.today.budget': 'budget',
    'ledger.today.breach': 'Over budget',
    'ledger.today.noStress': 'No stress scenario written down yet.',
    'ledger.today.triggers': 'The lines you wrote down',
    'ledger.today.triggerBreached': 'Triggered',
    'ledger.today.triggerOk': 'Not triggered',
    'ledger.today.noTriggers': 'No line written down yet.',
    'ledger.today.partial': 'No price for {syms}, so they are left out of E, L and stress loss.',
    'ledger.f.stress.scenarios': 'Stress scenarios',
    'ledger.f.stress.scenarios.name': 'Stress scenario name',
    'ledger.f.stress.scenarios.index_drop_pct': 'Stress scenario index decline %',
    'ledger.f.framework.energy.metric': 'Energy metric',
    'ledger.today.record': 'Enter',
    'ledger.today.downgradeAt': 'Downgrades when:',
    'ledger.today.upgradeRequires': 'To move up:',
    'ledger.today.loading': 'Loading status...',
    'ledger.footer': 'This is a check of the rules you wrote down against the data for today, not a recommendation.',
    'ledger.scenario.A': 'Both drivers confirmed',
    'ledger.scenario.B': 'Incomplete',
    'ledger.scenario.C': 'Framework invalid',
    'ledger.state.pass': 'pass',
    'ledger.state.fail': 'fail',
    'ledger.state.unknown': 'unknown',
    'ledger.state.invalidated': 'invalidated',
    'ledger.cond.inflation': 'Inflation',
    'ledger.cond.energy': 'Energy',
    'ledger.cond.policy': 'Policy',
    'ledger.cond.rates': 'Rates',
    'ledger.cond.earnings': 'Earnings',
    'ledger.calc.fedwatch.title': 'FedWatch weighted midpoint',
    'ledger.calc.fedwatch.meeting': 'Meeting date',
    'ledger.calc.fedwatch.meeting.help': 'How to set this: the FOMC date the probabilities you are reading belong to.',
    'ledger.calc.fedwatch.m0': 'Current target midpoint (%)',
    'ledger.calc.fedwatch.m0.help': 'How to set this: the midpoint of the target range in force today, as the Fed states it.',
    'ledger.calc.fedwatch.validUntil': 'Entry valid until',
    'ledger.calc.fedwatch.validUntil.help': 'How to set this: the date after which this hand-entered reading should stop counting. It is prefilled from your validity setting.',
    'ledger.calc.fedwatch.outcomes': 'Outcomes',
    'ledger.calc.fedwatch.outcomes.help': 'One row per rate outcome you are reading, up to six: the midpoint of that outcome and the probability shown beside it.',
    'ledger.calc.fedwatch.midpoint': 'midpoint %',
    'ledger.calc.fedwatch.prob': 'probability',
    'ledger.calc.fedwatch.addRow': '+ Add outcome',
    'ledger.calc.fedwatch.view': 'Your note',
    'ledger.calc.fedwatch.view.help': 'How to set this: one line on where you read these numbers and what you took from them.',
    'ledger.calc.fedwatch.submit': 'Save reading',
    'ledger.calc.fedwatch.muT': 'Weighted midpoint',
    'ledger.calc.fedwatch.deltaR': 'Change from the current midpoint',
    'ledger.calc.fedwatch.netHikes': 'Net hikes priced',
    'ledger.calc.fedwatch.changeVsPrev': 'Change vs your last entry',
    'ledger.calc.fedwatch.notComparable': 'Not comparable - different meeting',
    'ledger.calc.stress.title': 'Stress loss',
    'ledger.calc.stress.indexDrop': 'Index decline (%)',
    'ledger.calc.stress.indexDrop.help': 'How to set this: the index decline you want priced. Loss is market value times beta times that decline.',
    'ledger.calc.stress.betaAssumed': 'Beta taken as 1 where no beta is available.',
    'ledger.calc.stress.noHoldings': 'Holdings with market values appear here once the status cards can run.',
    'ledger.calc.stress.total': 'Total stress loss',
    'ledger.review.empty': 'A review appears here once a journal entry bound to an event has settled.',
    'ledger.review.q.scenario_judgement': 'Scenario: what did you write down before the event, and what did the data show after it?',
    'ledger.review.q.rate_shock': 'Rates: how did the yield path move against the reading you had recorded?',
    'ledger.review.q.profit_mgmt': 'Profit handling: what did you do with the gain or the loss, and was it what you had written?',
    'ledger.review.q.account_risk': 'Account risk: did E, L or stress loss cross a line you had written down?',
    'ledger.review.q.exec_cost': 'Execution cost: spreads, slippage and financing over this cycle.',
    'ledger.review.q.vs_baseline': 'Versus holding unchanged: how did what you did compare with leaving the position alone?',
    'ledger.review.judged': 'Was your call correct?',
    'ledger.review.yes': 'Yes',
    'ledger.review.no': 'No',
    'ledger.review.unknown': 'Not decided',
    'ledger.review.save': 'Save review',
    'ledger.review.saved': 'Saved.',
  },
  zh: {
    'brief.kind.earnings': '财报',
    'brief.kind.exDiv': '除息',
    'brief.session.mid': '盘中公布',
    'brief.carryover': '承接：',
    'brief.closingWatch': '午后：',
    'brief.newSinceYesterday': '较昨日新增',
    'brief.diffFrom': '不在 {d} 的关注清单里',
    'events.overlay.exDiv': '{sym} 除息',
    'events.overlay.exDivAmount': ' · 每股 ${v}，上次派付',
    'analyze.stage.event_pricing': '事件与期权定价',
    'analyze.stage.data_unavailable': '数据不可用',
    'analyze.linesRead': '项读数',
    'analyze.evidenceGrade': '证据 {g} 级',
    'analyze.notCalculated': '未计算',
    'analyze.eventPricing.title': '事件与期权定价',
    'analyze.eventPricing.reportDate': '财报日',
    'analyze.eventPricing.countdown': '{n} 个交易日后',
    'analyze.eventPricing.impliedMove': '隐含波幅',
    'analyze.eventPricing.atmIv': '平值 IV',
    'analyze.eventPricing.ivRank': 'IV 分位',
    'analyze.eventPricing.histMedianMove': '历史中位波幅',
    'analyze.eventPricing.samples': '取自 {n} 次历史财报',
    'analyze.fundamentals.title': '基本面',
    'analyze.fundamentals.note': '已申报数值及其所属期间。它们描述公司，不为公司打分。',
    'analyze.peers.title': '同业',
    'analyze.peers.rank': '第 {r} / 共 {n}',
    'analyze.peers.percentile': '{p} 分位',
    'analyze.peers.note': '这是在数据源给出的同业集合内的位置，不是对公司的判断。',
    'analyze.unavailable.title': '未能读取的部分',
    'macro.title': '宏观指标',
    'macro.nextFomc': '下次议息决议 {d}',
    'macro.source': '数据：{d} · {s}',
    'macro.source.tv': 'TradingView 日收盘价',
    'macro.label.wti': 'WTI 原油',
    'macro.label.brent': '布伦特原油',
    'macro.label.gold': '黄金',
    'macro.meaning.gold': '现货黄金价格，单位为美元/盎司。',
    'macro.meaning.brent': 'ICE 布伦特原油最近月期货合约的价格，国际原油的主要基准。',
    'macro.label.us10y': '10年美债收益率',
    'macro.unit.bp': '基点',
    'macro.unit.pt': '点',
    'macro.unch': '持平',
    'macro.prev': '前值 {v}',
    'macro.vsReading': '{c}，较{d}。',
    'macro.header': '{d}收盘 · 涨跌较前值',
    'macro.monthData': '{d}数据',
    'macro.asOf': '数据截至 {d}',
    'macro.source.oil': '近月期货，美元/桶 · TradingView 日收盘价',
    'macro.source.gold': '现货，美元/盎司 · TradingView 日收盘价',
    'macro.rollDay': '换月日',
    'macro.vsClose': '{c}，较{d}收盘。',
    'macro.bpNote': '1 基点 = 0.01 个百分点。',
    'macro.label.t10y2y': '10年减2年利差',
    'macro.label.breakeven_10y': '10年盈亏平衡通胀',
    'macro.label.cpi_yoy': 'CPI 同比',
    'macro.label.core_cpi_yoy': '核心 CPI 同比',
    'macro.label.fed_funds': '联邦基金利率',
    'macro.label.vix': 'VIX',
    'macro.label.dxy': '美元指数',
    'macro.meaning.wti': 'WTI 原油最近月期货合约的价格，美国原油的主要基准。能源价格会传导到通胀，也会进入许多公司的成本。',
    'macro.meaning.us10y': '10 年期美国国债的市场收益率。房贷利率和股票估值模型普遍以它为定价基准。',
    'macro.meaning.t10y2y': '10 年期收益率减 2 年期收益率。为正表示长期利率高于短期；低于零称为收益率曲线倒挂。',
    'macro.meaning.breakeven_10y': '10 年期盈亏平衡通胀率：10 年期国债名义收益率减去同期限 TIPS 收益率，即两者回报相同的年化通胀率。它是债券市场的通胀补偿，含风险与流动性溢价，不等同于纯粹的通胀预测。',
    'macro.meaning.cpi_yoy': '消费者价格（CPI-U，全部项目）相对去年同月的涨幅。按月发布，通常在次月中旬公布。',
    'macro.meaning.core_cpi_yoy': '剔除食品和能源后的 CPI 相对去年同月的涨幅。',
    'macro.meaning.fed_funds': '联邦基金有效利率：银行间隔夜无抵押准备金拆借利率的成交量加权中位数，维持在美联储目标区间内。许多贷款和存款利率随它变动。',
    'macro.meaning.vix': 'CBOE 波动率指数：由标普 500 期权价格推算的未来 30 天年化预期波动率。数值越高，市场定价的波动越大。',
    'macro.meaning.dxy': 'ICE 美元指数：美元相对六种货币固定权重一篮子的汇率，欧元约占 58%。美元走强会压低美国公司海外收入折算成美元后的金额。',
    'attention.dismiss': '暂不处理',
    'attention.inTradingDays': '{n} 个交易日后',
    'attention.fullCalendar': '完整 45 天日历在事件页 →',
    'attention.exposure.position': '{n} 个仓',
    'attention.exposure.positions': '{n} 个仓',
    'attention.exposure.notional': '名义本金 {v}',
    'attention.exposure.furthest': '最远到期 {d}',
    'money.stocks': '股票市值',
    'money.positions': '{n} 笔',
    'money.withQuote': '{total} 笔中 {n} 笔有报价',
    'money.unrealized': '未实现',
    'money.withCost': '{total} 笔中 {n} 笔有成本',
    'money.lastSession': '上一交易日',
    'money.idleCash': '闲置现金',
    'money.heldForPuts': '看跌占用',
    'money.pendingAssignment': '待确认指派',
    'money.notInAnyTotal': '不计入任何合计',
    'money.capitalAtRisk': '在险资金',
    'money.netAccount': '净账户',
    'money.margin': '融资',
    'money.maintReq': '维持保证金',
    'money.excessLiquidity': '超额流动性',
    'nav.search.placeholder': '代码',
    'nav.search.label': '前往标的页面',
    'learn.title': '课程',
    'learn.subtitle': '应用内课程，一次读一篇。',
    'learn.loading': '加载中……',
    'learn.unavailable': '课程内容暂不可用。',
    'learn.minutes': '{n} 分钟',
    'learn.more': '更多',
    'learn.glossary': '术语全表',
    'learn.glossary.note': '站内带下划线的术语都指向这里。',
    'brief.moverReason.earnings': '财报',
    'brief.moverReason.uoa': '期权异动',
    'brief.col.actualEps': '实际 EPS',
    'brief.col.surprise': '超预期',
    'app.title.default': 'NavOption - 期权投资助手',
    'app.title.scan': '扫描器 - NavOption',
    'app.title.pricing': '定价 - NavOption',
    'app.title.account': '账户 - NavOption',
    'aria.footer': '页脚',
    'aria.language': '语言',
    'aria.mainNav': '主导航',
    'aria.marketSnapshot': '市场快照',
    'brand.subtitle': '期权研究',
    'common.backHome': '返回首页',
    'common.free': '免费',
    'common.pro': '专业版',
    'common.signInCreate': '登录 / 创建账户',
    'footer.legal': '仅用于教育、筛选和研究，不构成财务建议。数据可能延迟或不完整。',
    'language.en': 'EN',
    'language.zh': '中文',
    'market.afterHours': '盘后',
    'market.closed': '休市',
    'market.open': '开盘',
    'market.opensSoon': '即将开盘',
    'market.preMarket': '盘前',
    'nav.account': '账户',
    'nav.analyze': '个股分析',
    'nav.brief': '每日简报',
    'nav.earnings': '财报',
    'nav.learn': '学习',
    'nav.login': '登录',
    'nav.markets': '行情',
    'nav.curriculum': '课程',
    'aria.navGroup': '展开菜单',
    'nav.news': '新闻',
    'nav.notes': '实战笔记',
    'nav.positions': '持仓',
    'nav.pricing': '定价',
    'nav.privacy': '隐私',
    'nav.research': '研究',
    'nav.scan': '扫描',
    'nav.support': '支持',
    'nav.terms': '条款',
    'nav.tools': '工具',
    'nav.uoa': '异动',
    'nav.watchlist': '自选',
    'nav.wheelTools': '轮转工具',
    'promo.badge': '新',
    'promo.cta.account': '到账户兑换 ->',
    'promo.cta.signIn': '登录后兑换 ->',
    'promo.text': 'NavOption v2 已上线 - 使用 LAUNCH30 可免费体验 30 天 Pro。',
    'gate.ai.body': '个股分析会查看已申报的财务报表，登录后即可使用。免费账户也包含分析次数。',
    'gate.ai.title': '登录后运行个股分析',
    'gate.brief.body': '登录后即可查看每日盘前、盘中、盘后简报 —— 大盘复盘、异动流、财报与当日关注清单，一站汇总。',
    'gate.brief.title': '登录查看每日简报',
    'brief.title.premarket': '每日盘前简报',
    'brief.title.midday': '每日盘中简报',
    'brief.title.postclose': '每日盘后简报',
    'brief.marketDirection': '大盘方向',
    'brief.dataCutoff': '数据截至',
    'brief.loading': '报告加载中...',
    'brief.degraded': 'AI 解读暂不可用，以下为数据速览',
    'brief.executive.title': '本期要点',
    'brief.executive.observations': '值得留意',
    'brief.executive.watches': '接下来关注什么',
    'brief.executive.evidence': '查看依据',
    'brief.executive.condition': '检查条件',
    'brief.executive.recheck': '重新检查',
    'brief.executive.observe': '查看依据',
    'brief.coverage.title': '报告覆盖',
    'brief.coverage.available': '个章节有可用内容',
    'brief.coverage.note': '缺少章节表示本报告没有可用内容，可能是时段安静，也可能是数据不可用；不能仅凭此判断数据源发生故障。',
    'brief.coverage.missing': '无可用内容：',
    'brief.coverage.freshness': '数据日期',
    'brief.coverage.current': '报告当日',
    'brief.coverage.prior': '更早数据',
    'brief.coverage.unknown': '未知',
    'brief.coverage.unknownDate': '日期未知',
    'brief.coverage.currentNote': '“报告当日”仅表示数据日期与本报告交易日一致，不代表实时数据。',
    'brief.coverage.separator': '、',
    'brief.section.closeRecap': '市场概览',
    'brief.section.movers': '市场异动',
    'brief.section.keyLevels': '期权持仓分布',
    'brief.section.optionFlow': '期权流',
    'brief.section.earningsRecap': '财报回顾',
    'brief.section.tomorrowWatch': '下一交易日关注',
    'brief.section.macroWatch': '宏观日历',
    'brief.section.todayEarnings': '今日财报',
    'brief.section.earningsIvWatch': '财报 IV 观察',
    'brief.section.uoaMorning': '早盘期权异动',
    'brief.section.middayMarket': '盘中市场',
    'brief.section.other': '报告详情',
    'brief.col.index': '指数',
    'brief.col.sector': '板块',
    'brief.col.close': '收盘价',
    'brief.col.change': '涨跌幅',
    'brief.col.symbol': '代码',
    'brief.col.side': '方向',
    'brief.col.strike': '行权价',
    'brief.col.dte': 'DTE',
    'brief.col.notional': '权利金成交额',
    'brief.col.multiple': '量比',
    'brief.col.price': '现价',
    'brief.col.premium': '权利金',
    'brief.col.earningsFlag': '财报',
    'brief.col.estEps': '预期EPS',
    'brief.col.estRev': '预期营收',
    'brief.col.atmIv': '平值 IV',
    'brief.col.ivChange': 'IV变化',
    'brief.col.event': '事件',
    'brief.col.kind': '类型',
    'brief.col.impact': '影响',
    'brief.col.when': '时间',
    'brief.col.apy': '年化(快照)',
    'brief.col.iv': 'IV',
    'brief.tier.smart_flow': '聪明钱流',
    'brief.tier.contrarian': '逆势',
    'brief.tier.lottery': '彩票',
    'brief.tier.bearish_hedge': '对冲',
    'brief.session.bmo': '盘前公布',
    'brief.session.amc': '盘后公布',
    'brief.col.ivPercentile': 'IV 百分位(90日)',
    'brief.col.earningsDate': '财报日',
    'brief.ivPercentileDisclosure': 'IV 百分位仅描述该股自身近 90 日的相对位置，非涨跌预测。',
    'brief.snapshotAsOf': '昨收快照',
    'brief.slotAria': '报告时段',
    'brief.slot.premarket': '盘前',
    'brief.slot.midday': '盘中',
    'brief.slot.postclose': '盘后',
    'brief.positioning.premarket': '盘前概览——今日日程与昨收背景，非交易建议。',
    'brief.positioning.midday': '盘中速览——当日行情如何演进，非交易建议。',
    'brief.positioning.postclose': '盘后市场回顾——今天发生了什么，非交易建议。',
    'brief.empty.premarket': '盘前简报在交易日美东时间 08:30 后生成，请稍后再来查看。',
    'brief.empty.midday': '盘中简报在交易日美东时间 12:30 后生成（提前收盘日不生成）。',
    'brief.empty.postclose': '今日盘后简报约美东 17:15 生成，请届时再来查看。',
    'brief.emptyGoto': '或查看其他简报：',
    'brief.middayAfterCloseHint': '盘后简报约美东 17:15 生成。',
    'brief.priorTradingDay': '上一交易日',
    'brief.checkup': '个股分析',
    'brief.breadth.advancers': '上涨',
    'brief.breadth.decliners': '下跌',
    'brief.breadth.unchanged': '平盘',
    'brief.breadth.universePrefix': '本平台跟踪的 ',
    'brief.breadth.universeSuffix': ' 只期权流动性标的中：',
    'brief.breadth.notMarketWide': '（非全市场宽度）',
    'brief.news.none': '当日无匹配到的公开标题',
    'brief.news.unavailable': '今日消息面数据不可用',
    'brief.news.error': '消息面未获取',
    'brief.movers.help': '标题只是当天同期发布的内容，不代表它导致了这次涨跌。',
    'brief.col.spot': '现价',
    'brief.col.putWall': '看跌持仓最密集价位',
    'brief.col.callWall': '看涨持仓最密集价位',
    'brief.col.topStrikes': '持仓最密集价位',
    'brief.col.snapshotTs': '快照时点（ET）',
    'brief.keyLevels.disclosurePrefix': '按 ',
    'brief.keyLevels.disclosureSuffix': ' ET 的链快照计算，未平仓合约为上一结算日口径（T+1）。总额（gross）口径，跨标的、跨日不可直接比较。只说明持仓分布在哪里，不代表价格会在此转向。',
    'brief.flow.verifiedBadge': 'D-1 已验证',
    'brief.flow.verifiedLabelPrefix': '逐笔大单/扫单结构，数据交易日 ',
    'brief.flow.verifiedLabelSuffix': '。当日逐笔要到 T+1 才可得。',
    'brief.flow.unusualLabelPrefix': '当日成交异动：',
    'brief.flow.help': '大单 = 一次成交的大额单笔；扫单 = 同一时刻扫过多个报价的单子。已剔除多腿组合单，只保留单腿。',
    'brief.flow.topPrintPrefix': '最大单笔 ',
    'brief.flow.topPrintSuffix': ' · 展开逐笔',
    'brief.unit.contracts': '张',
    'brief.cond.crossNonIso': '单腿撮合（非 ISO）',
    'brief.cond.auctionNonIso': '单腿竞价（非 ISO）',
    'brief.cond.floorTrade': '场内人工',
    'brief.cond.autoExecution': '自动执行',
    'brief.cond.intermarketSweep': '跨市扫单',
    'brief.col.blocks': '大单笔数',
    'brief.col.sweeps': '扫单笔数',
    'brief.col.blockPremium': '大单权利金',
    'brief.col.sweepPremium': '扫单权利金',
    'brief.col.cleanVolume': '净成交量',
    'brief.col.multiLeg': '多腿占比',
    'brief.group.earnings': '财报',
    'brief.group.dividends': '明日除息',
    'brief.group.wheelPicks': 'Wheel 摘录',
    'brief.col.exDivDate': '除息日',
    'brief.col.divAmount': '每股股息',
    'brief.col.divYield': '股息率',
    'analyze.stop': '停止',
    'analyze.stage.stopped': '已停止',
    'analyze.perMonth': '/月',
    'analyze.deepTitle': '深度分析增加内部人历史、同业对比、波动区间和通俗解读。会消耗一次每月深度分析额度。',
    'analyze.kicker': '个股分析',
    'analyze.title': '个股分析',
    'analyze.intro.pro': '查看现金流、债务、营收和价格波动。Pro 增加内部人历史、同业对比和波动区间。',
    'analyze.intro.free': '查看现金流、债务、营收和价格波动。结果会列出发现的风险和无法检查的数据。',
    'analyze.sourceNote': '数据来自已申报的财务报表，而非期权链。',
    'analyze.run': '开始分析',
    'analyze.running': '分析中……',
    'analyze.loading': '正在查看可用的财务和市场数据……',
    'analyze.outcome.vetoed': '发现风险',
    'analyze.outcome.marginal': '需要进一步了解',
    'analyze.outcome.pass': '未发现明显问题',
    'analyze.outcome.unknown': '数据不足',
    'analyze.outcome.unavailable': '暂无结果',
    'analyze.coverage': '数据覆盖',
    'analyze.checksAvailable': '项检查有可用数据',
    'analyze.laterChecks': '发现风险后，后续检查未继续执行，因此这些问题仍待确认。',
    'analyze.scopeNote': '本结果仅涵盖基于可用数据完成的检查，不代表公司估值，也不构成买入或卖出建议。',
    'analyze.check.vetoed': '发现风险',
    'analyze.check.marginal': '需要进一步了解',
    'analyze.check.pass': '处于参考范围',
    'analyze.check.unknown': '数据不足',
    'analyze.notReported': '未报告',
    'analyze.noScore': '无评分',
    'analyze.log.rawToggle': '原始事件日志',
    'analyze.log.idle': '空闲。',
    'analyze.stage.mode': '开始分析',
    'analyze.stage.stock': '读取行情数据',
    'analyze.stage.fundamentals': '基本面分析',
    'analyze.stage.news': '扫描新闻',
    'analyze.stage.peer_compare': '同业对比',
    'analyze.stage.insider': '内部交易',
    'analyze.stage.done': '完成',
    'analyze.stage.error': '出错',
    'analyze.stage.unknown': '处理中',
    'home.actions.analyze': '分析个股',
    'home.actions.pricing': '比较套餐',
    'home.actions.scan': '浏览收益机会',
    'home.eyebrow': '把轮转策略变简单',
    'home.headline': '研究你熟悉股票的轮转交易。',
    'home.body': '比较主要美股的轮转交易，查看现金流、债务、营收和价格波动，再从开仓到退出跟踪每笔持仓。权利金可以在等待时带来收入，但仍可能发生被行权和市场亏损。',
    'auth.apple.login': '使用 Apple 登录',
    'auth.apple.register': '使用 Apple 注册',
    'auth.body.login': '使用 Apple 或 Google 登录，在 Web 和 iOS 之间同步你的工作区。',
    'auth.body.register': '一个账户同时支持 Web 和 iOS。用 Apple 或 Google 注册后，可保存扫描器、自选、提醒和持仓。',
    'auth.google.login': '使用 Google 登录',
    'auth.google.register': '使用 Google 注册',
    'auth.loadingApple': 'Apple 登录仍在加载，请稍后再试。',
    'auth.loadingGoogle': 'Google 登录仍在加载，请稍后再试。',
    'auth.message.appleFailed': 'Apple 登录失败。',
    'auth.message.appleTokenMissing': 'Apple 未返回身份令牌。',
    'auth.message.googleCodeMissing': 'Google 未返回授权码。',
    'auth.message.signingInApple': '正在使用 Apple 登录...',
    'auth.message.signingInGoogle': '正在使用 Google 登录...',
    'auth.title.login': '登录',
    'auth.title.register': '创建账户',
    'account.eyebrow': '账户',
    'account.heading': '你的 NavOption 工作区。',
    'account.loading': '正在加载工作区...',
    'account.signingOut': '正在退出登录...',
    'notFound.body': '这个页面不存在或已移动。',
    'notFound.title': '页面未找到',
    'nav.events': '事件',
    'nav.journal': '日志',
    'events.kicker': '事件',
    'events.title': '事件周期',
    'events.subtitle': '未来 45 天已公布的宏观日程，叠加你的到期日与财报日。',
    'events.next.label': '下一事件',
    'events.next.tradingDays': '还有 {n} 个交易日',
    'events.timeline.title': '时间线',
    'events.empty': '未来 45 天没有已公布的事件。',
    'events.overlay.expiry': '你的 {sym} 期权到期',
    'events.overlay.earnings': '{sym} 财报',
    'events.overlay.holding': '（持股）',
    'events.overlay.position': '（持仓）',
    'events.overlay.spans': '跨 {label}',
    'events.overlay.empty': '添加持仓或持股后，到期日和财报日会叠加在这里。',
    'events.readings.title': '读数',
    'events.reading.rank20d': '过去一年 20 日变化的第 {n} 分位',
    'events.reading.rank5d': '过去一年 5 日变化的第 {n} 分位',
    'events.reading.rankMonthly': '过去一年月度变化的第 {n} 分位',
    'events.reading.statMonth': '统计月 {m}',
    'events.reading.released': '公布日 {d}',
    'events.asOf': '数据日期 {d}',
    'holdings.title': '股票',
    'holdings.add': '+ 添加持股',
    'holdings.cancel': '取消',
    'holdings.save': '保存',
    'holdings.saving': '保存中...',
    'holdings.symbol': '代码',
    'holdings.qty': '股数',
    'holdings.costBasis': '每股成本',
    'holdings.bucket': '归属',
    'holdings.bucket.core': '底仓',
    'holdings.bucket.event': '事件仓',
    'holdings.bindEvent': '绑定事件',
    'holdings.noEvent': '不绑定',
    'holdings.thesis': '持有理由',
    'holdings.optional': '可空',
    'holdings.check': '分析',
    'holdings.edit': '编辑',
    'holdings.delete': '删除',
    'holdings.empty': '还没有股票持股。',
    'holdings.confirmDelete': '要删除 {sym} 这笔持股吗？',
    'holdings.notSet': '未填',
    'holdings.shares': '股',
    'holdings.account.title': '账户',
    'holdings.account.netLiq': '账户净值',
    'holdings.account.marginDebt': '融资余额',
    'holdings.account.reservedCash': '不得动用的资金',
    'holdings.account.maintenancePct': '维持保证金率 %',
    'holdings.assign.kicker': '被行权',
    'holdings.assign.title': '记录为持股？',
    'holdings.assign.strike': '行权价',
    'holdings.assign.premiums': '累计权利金',
    'holdings.assign.skip': '跳过',
    'holdings.callCandidates': '备兑候选',
    'holdings.callCandidates.hide': '收起',
    'holdings.value': '市值',
    'holdings.row.costBasis': '成本 {v}',
    'holdings.row.unrealized': '浮动盈亏 {v}（{pct}）',
    'cc.title': '{sym} 的备兑看涨',
    'cc.subtitle': '{qty} 股 · 够开 {n} 张',
    'cc.subtitle.one': '{qty} 股 · 够开 1 张',
    'cc.openCalls.dte': '{d} 天',
    'cc.openCalls.one': '这些股票上已经有 1 张 call：{list}',
    'cc.openCalls.many': '这些股票上已经有 {n} 张 call：{list}',
    'cc.loading': '正在取候选...',
    'cc.error': '取不到备兑看涨候选，过一会儿再试。',
    'cc.costBasis': '每股成本 {v}',
    'cc.costBasis.missing': '没有记录每股成本',
    'cc.assignedAt': '被行权价 {v}',
    'cc.currentPrice': '现价 {v}',
    'cc.lastClose': '最新收盘 {v}',
    'cc.vsCost': '相对成本 {v}',
    'cc.vsAssigned': '相对被行权价 {v}',
    'cc.ref.cost': '你的成本',
    'cc.ref.assigned': '你的被行权价',
    'cc.depth.spot': '没有成本记录，行权价下沿就取现价——比现价还低的 call 一开就已经在价内。',
    'cc.depth.above': '比{ref}高 {amt}——行权价从现价起步。',
    'cc.depth.shallow': '比{ref}低 {pct}，还在 SOP 卖 call 的 5% 区间里。',
    'cc.depth.deep': '比{ref}低 5% 以上——行权价不低于它的 call 往往根本不存在，而 SOP 也写明：坑到这个深度，卖 call 只能填回几个百分点。',
    'cc.rule': '只看 {min}–{max} 天到期、行权价 ≥ {floor}、未平仓量 ≥ {oi} 的 call。',
    'cc.row.strike': '{strike} · 虚值 {otm} · {date}（{dte} 天）',
    'cc.row.premium': '权利金 {premium} · 年化 {apy} · Δ{delta} · 未平仓量 {oi}',
    'cc.row.ifCalled': '被叫走：{value} · 相对{ref} {pct}',
    'cc.vsRef.cost_basis': '成本',
    'cc.vsRef.assigned_strike': '被行权价',
    'cc.vsRef.spot': '现价',
    'cc.empty': '没有 call 满足上面这条规则。SOP 的做法是 5 个交易日后再看一次。',
    'cc.empty.noChain': '我们没有收 {sym} 的期权链。',
    'cc.footer': '这不是下单。列表按权利金排序，最贴近现价的行权价排在最前——也就是最可能被叫走的那些。',
    'regime.title': '市场状态',
    'regime.state.recovery': '修复',
    'regime.state.stress_up': '压力上行',
    'regime.state.calm_up': '平稳上行',
    'regime.state.bear': '熊市',
    'regime.state.correction': '回调',
    'regime.asOf': '截至{d}收盘',
    'regime.above': 'SPY 在 200 日均线上方',
    'regime.below': 'SPY 在 200 日均线下方',
    'regime.dd': '距最高收盘价 {v}%',
    'regime.rv': '21 日已实现波动率 {v}%',
    'regime.sop': '本状态下 Wheel SOP 的节奏：',
    'regime.sop.recovery': '常规节奏——行权价虚值 15% 以上，7–14 天到期，不加杠杆凑收益。',
    'regime.sop.stress_up': '常规节奏——行权价虚值 15% 以上，7–14 天到期；已实现波动率新上穿 25% 就启动风险体检。',
    'regime.sop.calm_up': '常规节奏——行权价虚值 15% 以上，7–14 天到期，不加杠杆凑收益。',
    'regime.sop.bear': '行权价可以落到 0–5% 虚值，仍是 7–14 天到期——前提是你此前留了现金。',
    'regime.sop.correction': '维持常规节奏——唯一一次样本外检验把样本内规则的符号反了过来。',
    'regime.sop.source': '出自 Wheel SOP',
    'scan.cash': '所需现金',
    'scan.cash.shares': '用股票',
    'scan.maxCash': '每张最多占用现金',
    'scan.maxCash.putsOnly': '仅看跌可用',
    'scan.maxCash.note': '行权价 × 100，未扣掉收到的权利金。',
    'attention.source': '来自你的自选、持股与持仓',
    'attention.holding.earnings': '你持有 {sym} — {when}财报',
    'attention.impliedMove': '期权隐含 ±{v}%',
    'attention.when.today': '今天',
    'attention.when.inDays': '{d} 天后',
    'attention.new': '新',
    'attention.density.share': '{n} 股',
    'attention.density.shares': '{n} 股',
    'attention.density.contract': '{n} 张',
    'attention.density.contracts': '{n} 张',
    'attention.density.vsStrike': '距行权价 {v}%',
    'attention.rv21': 'RV21 {v}%，前值 {p}%',
    'attention.volCheck.open': '风险体检',
    'attention.today': '今天 · {d}',
    'attention.verdict.breach': '账户风控已触发——先降风险',
    'attention.verdict.items1': '今天有 1 件事需要你处理',
    'attention.verdict.items': '今天有 {n} 件事需要你处理',
    'attention.verdict.clear': '今天没有规则要求你行动',
    'attention.verdict.clearSub': '不交易也是有效选择。',
    'attention.tile.nextEvent': '下一重要事件',
    'attention.tile.noEvent': '未来 14 天没有',
    'attention.tile.spans1': '你有 1 个仓跨过它',
    'attention.tile.spans': '你有 {n} 个仓跨过它',
    'attention.tile.scenario': '市场情景',
    'attention.tile.account': '账户风险',
    'attention.tile.stress': '压力损失 {v} / 预算 {b}',
    'attention.tile.ledgerOff': '未开启',
    'attention.tile.ledgerOffSub': '填写损失预算后，这里显示敞口与压力测试 →',
    'attention.breach.next': '按预案降风险 →',
    'attention.scenario.means.A': '可考虑事件仓；融资默认关闭',
    'attention.scenario.means.B': '暂停新增事件仓；本策略融资降至 0',
    'attention.scenario.means.C': '停用本轮策略；按预案退出事件仓',
    'attention.tile.exposure': '敞口 {v}',
    'attention.tile.margin': '融资 {v}',
    'attention.tile.elHint': '敞口 = 股票市值 ÷ 账户净值（E）；融资 = 借款 ÷ 账户净值（L）。',
    'attention.col.next': '接下来',
    'attention.flow': '你标的上的异常流 · {n}',
    'attention.flow.baseline': '基线 {v}×',
    'attention.flow.held': '持有',
    'attention.flow.watched': '自选',
    'positions.markAssigned': '被行权',
    'positions.confirmAssigned': '把这笔 {sym} {side} 标记为被行权？',
    'positions.markExercised': '已行权',
    'positions.confirmExercised': '把这笔 {sym} 买入 Call 标记为已行权？',
    'positions.side.csp': '现金担保看跌',
    'positions.side.cc': '备兑看涨',
    'positions.side.lc': '买入 Call',
    'positions.side.lcNote': '买入 Call 的权利金，是你每股已付的金额。',
    'positions.atRisk': '在险资金 {v}',
    'symbol.previousClose': '上一个收盘价',
    'symbol.fiveDayMove': '5 日涨跌',
    'symbol.volume': '成交量',
    'symbol.range52w': '52 周区间',
    'symbol.avgChainIv': '平均期权链 IV',
    'symbol.bestApy': '最佳 APY',
    'symbol.dividendYield': '股息率',
    'journal.kicker': '日志',
    'journal.title': '决策日志',
    'journal.subtitle': '你做了什么、为什么这么做，以及之后标的怎么走。',
    'journal.kind': '类型',
    'journal.kind.option': '期权',
    'journal.kind.stock': '股票',
    'journal.kind.noTrade': '不交易',
    'journal.position': '持仓',
    'journal.choosePosition': '选择一个未平仓持仓',
    'journal.holding': '持股',
    'journal.chooseHolding': '选择一笔持股',
    'journal.symbol': '代码',
    'journal.action': '动作',
    'journal.bindEvent': '绑定事件',
    'journal.noEvent': '不绑定',
    'journal.eventEarnings': '财报',
    'journal.reason': '原因',
    'journal.reason.placeholder': '当时这么判断的理由',
    'journal.save': '记一笔',
    'journal.saving': '保存中...',
    'journal.delete': '删除',
    'journal.empty': '还没有记录。',
    'journal.quick.placeholder': '今天不出手的理由',
    'journal.quick.button': '今天决定不交易',
    'journal.action.open': '开仓',
    'journal.action.close': '平仓',
    'journal.action.roll': '移仓',
    'journal.action.assign': '被行权',
    'journal.action.buy': '买入',
    'journal.action.sell': '卖出',
    'journal.action.add': '加仓',
    'journal.action.reduce': '减仓',
    'journal.action.hold': '持有不动',
    'journal.snap.premium': '权利金',
    'journal.snap.ivRank': 'IV 分位',
    'journal.echo.bought': '买入后 {sym} 变动 {pct}（10 个交易日）',
    'journal.echo.sold': '卖出后 {sym} 变动 {pct}（10 个交易日）',
    'journal.echo.noTrade': '{sym} 变动 {pct}（10 个交易日）',
    'journal.echo.unavailable': '无收盘数据，未结算',
    'nav.ledger': '风险台账',
    'journal.logDecision': '记一笔',
    'ledger.kicker': '台账',
    'ledger.title': '事件仓风险台账',
    'ledger.subtitle': '用你自己写下的边界和框架条件，核对今天的数据。',
    'ledger.off.body': '适用于用普通股票 / 非杠杆 ETF 做事件仓的投资者，期权持仓不纳入。',
    'ledger.off.enable': '启用台账',
    'ledger.tab.today': '今日',
    'ledger.tab.settings': '设置',
    'ledger.tab.calc': '计算器',
    'ledger.tab.review': '复盘',
    'ledger.save': '保存设置',
    'ledger.saving': '保存中...',
    'ledger.choose': '请选择',
    'ledger.missing.title': '以下几项写下来之前，状态区保持只读：',
    'ledger.readonly': '设置补齐之前，状态区只读。',
    'ledger.step1': '第一步 · 账户边界与预算',
    'ledger.step2': '第二步 · 持仓归属与集中度',
    'ledger.step3': '第三步 · 框架条件与失效标准',
    'ledger.margin.title': '融资（默认关闭）',
    'ledger.margin.enable': '这个账户我确实用融资',
    'ledger.margin.help': '不用融资就保持关闭。关闭时融资负债比例 L 仍按账户快照显示，只是不核对融资上限。',
    'ledger.stress.title': '压力情景',
    'ledger.stress.help': '给每个你想每天核对的指数跌幅起个名字，并写下该情景里指数下跌多少。压力损失 = 市值 × β × 该跌幅。',
    'ledger.stress.name': '情景名称',
    'ledger.stress.drop': '指数跌幅 %',
    'ledger.stress.add': '+ 添加情景',
    'ledger.stress.remove': '删除',
    'ledger.metric.coreCpi': '核心 CPI 环比',
    'ledger.metric.cpi': 'CPI 环比',
    'ledger.consecutive.one': '一次公布',
    'ledger.consecutive.two': '连续两次公布',
    'ledger.energy.metricNote': '能源条件比较的是 WTI 相对最近一次已公布 CPI 统计月均价的变化。',
    'ledger.f.account.loss_budget_pct': '损失预算（占账户净值 %）',
    'ledger.h.account.loss_budget_pct': '怎么定：单个压力情景里，你能承受且不影响生活的损失。写成占账户净值的比例，不写金额。',
    'ledger.f.account.drawdown_warn_pct': '回撤警戒线（%）',
    'ledger.h.account.drawdown_warn_pct': '怎么定：从净值高点回落到多少时，你希望台账把账户标出来，而你当下仍然睡得着。',
    'ledger.f.account.drawdown_pause_pct': '回撤暂停线（%）',
    'ledger.h.account.drawdown_pause_pct': '怎么定：从净值高点回落到多少时，你此前已经决定不再新增事件仓敞口。',
    'ledger.f.account.equity_high_water': '净值高点',
    'ledger.h.account.equity_high_water': '怎么定：按你自己的记录，这个账户到过的最高净值。回撤以它为基准计算。',
    'ledger.f.account.exposure_cap_e': '股票总敞口倍数 E 上限',
    'ledger.h.account.exposure_cap_e': '怎么定：股票总市值相对账户净值，你愿意承受的最高倍数。',
    'ledger.f.account.single_name_cap_pct': '单一标的上限（%）',
    'ledger.h.account.single_name_cap_pct': '怎么定：任何一只标的占账户净值，你允许到达的最大比例。',
    'ledger.f.account.sector_cap_pct': '单一行业上限（%）',
    'ledger.h.account.sector_cap_pct': '怎么定：任何一个行业占账户净值，你允许到达的最大比例。',
    'ledger.f.margin.l_cap': '融资负债比例 L 上限',
    'ledger.h.margin.l_cap': '怎么定：融资余额相对账户净值，你愿意承受的最高比例。',
    'ledger.f.margin.cost_pct': '融资成本（年化 %）',
    'ledger.h.margin.cost_pct': '怎么定：券商对负债余额收取的年化利率，按对账单填。',
    'ledger.f.margin.maintenance_buffer_pct': '维持保证金缓冲（%）',
    'ledger.h.margin.maintenance_buffer_pct': '怎么定：在券商的维持保证金要求之上，你想留多少余量才会动手。',
    'ledger.f.margin.profit_trigger_pct': '盈利触发线（%）',
    'ledger.h.margin.profit_trigger_pct': '怎么定：事件仓盈利到多少时，你自己写下的规则要求重新审视融资水平。',
    'ledger.f.margin.post_profit_l_target': '触发后的融资负债比例 L',
    'ledger.h.margin.post_profit_l_target': '怎么定：盈利触发线达到之后，你写下要维持的那个 L。',
    'ledger.f.margin.post_profit_loss_budget_pct': '触发后的损失预算（%）',
    'ledger.h.margin.post_profit_loss_budget_pct': '怎么定：盈利触发线达到之后，取代原来那条的损失预算。',
    'ledger.f.framework.inflation.metric': '通胀口径',
    'ledger.h.framework.inflation.metric': '怎么定：选你实际跟踪的那个月度口径。台账从宏观序列读取，不读任何预测。',
    'ledger.f.framework.inflation.pass_max': '通胀通过线（≤ %）',
    'ledger.h.framework.inflation.pass_max': '怎么定：月度读数低于或等于多少时，你认为通胀这一项通过。',
    'ledger.f.framework.inflation.fail_above': '通胀失效线（> %）',
    'ledger.h.framework.inflation.fail_above': '怎么定：读数高于多少时，你此前已经认定框架里的通胀这条腿不再成立。',
    'ledger.f.framework.inflation.consecutive': '需要几次公布',
    'ledger.h.framework.inflation.consecutive': '怎么定：一次公布你就认，还是只认连续两次。',
    'ledger.f.framework.energy.pass_max': '能源通过线（≤ %）',
    'ledger.h.framework.energy.pass_max': '怎么定：WTI 相对 CPI 统计月均价的变化低于或等于多少时，你认为能源这一项通过。',
    'ledger.f.framework.energy.warn_above': '能源警戒线（> %）',
    'ledger.h.framework.energy.warn_above': '怎么定：该变化高于多少时你想被标出来，但还不算未通过。',
    'ledger.f.framework.energy.fail_above': '能源失效线（> %）',
    'ledger.h.framework.energy.fail_above': '怎么定：该变化高于多少时，你此前已经认定能源这条腿不再成立。',
    'ledger.f.framework.policy.target_meeting': '目标 FOMC 会议',
    'ledger.h.framework.policy.target_meeting': '怎么定：政策条件针对的那次会议。FedWatch 录入必须是同一次会议，才能和上一条比较。',
    'ledger.f.framework.policy.pass_max_net_hikes': '政策通过线（净加息次数 ≤）',
    'ledger.h.framework.policy.pass_max_net_hikes': '怎么定：市场对该次会议定价的净加息次数低于或等于多少时，你认为政策这一项通过。',
    'ledger.f.framework.policy.fail_above_net_hikes': '政策失效线（净加息次数 >）',
    'ledger.h.framework.policy.fail_above_net_hikes': '怎么定：净次数高于多少时，你此前已经认定政策这条腿不再成立。',
    'ledger.f.framework.policy.reading_ttl_days': 'FedWatch 录入有效期（天）',
    'ledger.h.framework.policy.reading_ttl_days': '怎么定：一条手工录入的 FedWatch 读数能用多久，过期后政策条件回到未知。',
    'ledger.f.framework.rates.warn_5d_bp': '10Y 五日变化警戒（bp）',
    'ledger.h.framework.rates.warn_5d_bp': '怎么定：10 年期收益率五个交易日的变动达到多少基点时，你想被标出来。',
    'ledger.f.framework.rates.fail_no_retrace_days': '未回撤多少交易日算失效',
    'ledger.h.framework.rates.fail_no_retrace_days': '怎么定：收益率越过警戒线后，可以有多少个交易日不回落，超过你就认定利率这条腿未通过。',
    'ledger.f.framework.rates.retrace_target_pct': '回撤幅度要求（%）',
    'ledger.h.framework.rates.retrace_target_pct': '怎么定：这轮变动要收回多少，你才算它回撤了。',
    'ledger.f.framework.earnings.universe': '盈利核对标的池',
    'ledger.h.framework.earnings.universe': '怎么定：盈利这一项要跑 Analyze 闸门的标的代码，用逗号分隔。',
    'ledger.today.accountCard': '账户硬风控',
    'ledger.today.frameworkCard': '市场框架',
    'ledger.today.e': '股票总敞口倍数 E',
    'ledger.today.l': '融资负债比例 L',
    'ledger.today.n': '账户净值',
    'ledger.today.drawdown': '回撤',
    'ledger.today.marginCall': '距追缴的跌幅',
    'ledger.today.bucketCore': '底仓',
    'ledger.today.bucketEvent': '事件仓',
    'ledger.today.longCall': '买入期权已付权利金 {v} · {n} 张',
    'ledger.today.longCallNote': '这是这些合约的最大亏损，不计入 E。',
    'ledger.today.stress': '压力损失',
    'ledger.today.budget': '预算',
    'ledger.today.breach': '超出预算',
    'ledger.today.noStress': '还没有写下压力情景。',
    'ledger.today.triggers': '你写下的警戒线',
    'ledger.today.triggerBreached': '已触发',
    'ledger.today.triggerOk': '未触发',
    'ledger.today.noTriggers': '还没有写下警戒线。',
    'ledger.today.partial': '{syms} 没有价格，未计入 E、L 与压力损失。',
    'ledger.f.stress.scenarios': '压力情景',
    'ledger.f.stress.scenarios.name': '压力情景名称',
    'ledger.f.stress.scenarios.index_drop_pct': '压力情景指数跌幅 %',
    'ledger.f.framework.energy.metric': '能源口径',
    'ledger.today.record': '录入',
    'ledger.today.downgradeAt': '降档条件：',
    'ledger.today.upgradeRequires': '升档需要：',
    'ledger.today.loading': '正在读取状态...',
    'ledger.footer': '以上是你写下的规则对今天数据的核对，不是操作建议。',
    'ledger.scenario.A': '双驱动条件具备',
    'ledger.scenario.B': '条件不完整',
    'ledger.scenario.C': '原框架失效',
    'ledger.state.pass': '通过',
    'ledger.state.fail': '未通过',
    'ledger.state.unknown': '未知',
    'ledger.state.invalidated': '失效',
    'ledger.cond.inflation': '通胀',
    'ledger.cond.energy': '能源',
    'ledger.cond.policy': '政策',
    'ledger.cond.rates': '利率',
    'ledger.cond.earnings': '盈利',
    'ledger.calc.fedwatch.title': 'FedWatch 加权中点',
    'ledger.calc.fedwatch.meeting': '会议日期',
    'ledger.calc.fedwatch.meeting.help': '怎么定：你手上这组概率对应的那次 FOMC 日期。',
    'ledger.calc.fedwatch.m0': '当前目标区间中点（%）',
    'ledger.calc.fedwatch.m0.help': '怎么定：当前生效目标区间的中点，按美联储的表述填。',
    'ledger.calc.fedwatch.validUntil': '录入有效期至',
    'ledger.calc.fedwatch.validUntil.help': '怎么定：这条手工读数到哪天之后不再计入。已按你设置的有效期预填。',
    'ledger.calc.fedwatch.outcomes': '结果分布',
    'ledger.calc.fedwatch.outcomes.help': '每个利率结果一行，最多六行：该结果的区间中点，以及它旁边显示的概率。',
    'ledger.calc.fedwatch.midpoint': '中点 %',
    'ledger.calc.fedwatch.prob': '概率',
    'ledger.calc.fedwatch.addRow': '+ 添加一行',
    'ledger.calc.fedwatch.view': '你的记录',
    'ledger.calc.fedwatch.view.help': '怎么定：一句话写清这组数字读自哪里、你从中看到了什么。',
    'ledger.calc.fedwatch.submit': '保存读数',
    'ledger.calc.fedwatch.muT': '加权中点',
    'ledger.calc.fedwatch.deltaR': '相对当前中点的变化',
    'ledger.calc.fedwatch.netHikes': '定价的净加息次数',
    'ledger.calc.fedwatch.changeVsPrev': '与你上一条的变化',
    'ledger.calc.fedwatch.notComparable': '不可比 · 会议不同',
    'ledger.calc.stress.title': '压力损失',
    'ledger.calc.stress.indexDrop': '指数跌幅（%）',
    'ledger.calc.stress.indexDrop.help': '怎么定：你想算的指数跌幅。损失 = 市值 × β × 该跌幅。',
    'ledger.calc.stress.betaAssumed': '无 β 数据的标的按 1 估算。',
    'ledger.calc.stress.noHoldings': '状态卡能跑起来之后，带市值的持股会出现在这里。',
    'ledger.calc.stress.total': '压力损失合计',
    'ledger.review.empty': '绑定事件的日志记录结算之后，复盘卡会出现在这里。',
    'ledger.review.q.scenario_judgement': '情景判断：事件前你写下的是什么，事件后数据显示了什么？',
    'ledger.review.q.rate_shock': '利率冲击：利率路径相对你当时记下的读数怎么走的？',
    'ledger.review.q.profit_mgmt': '盈利处理：赚到或亏掉的部分你怎么处理的，和你写下的一致吗？',
    'ledger.review.q.account_risk': '账户风险：E、L 或压力损失有没有越过你写下的线？',
    'ledger.review.q.exec_cost': '执行成本：这一轮的价差、滑点和融资成本。',
    'ledger.review.q.vs_baseline': '与原样持有相比：你做的这些和不动相比如何？',
    'ledger.review.judged': '判断是否正确',
    'ledger.review.yes': '正确',
    'ledger.review.no': '不正确',
    'ledger.review.unknown': '未判定',
    'ledger.review.save': '保存复盘',
    'ledger.review.saved': '已保存。',
  },
} as const

const staticZh: Record<string, string> = {
  ...generatedStaticZh,
  'NavOption': 'NavOption',
  'Effective May 16, 2026': '生效日期：2026 年 5 月 16 日',
  'Inside the product': '产品内览',
  'Three screens, one workflow.': '三个页面，一套流程。',
  'Idea Browser': '机会浏览器',
  'Stock Page': '股票页面',
  'Live from the scanner': '来自扫描器的实时数据',
  'What the wheel is paying right now.': '当前轮转策略正在支付什么收益。',
  'Top picks pulled live from the NavOption option chain. Click any card for the full thesis.': '从 NavOption 期权链实时拉取的精选机会。点击任一卡片查看完整逻辑。',
  'Cash-Secured Puts': '现金担保看跌',
  'Covered Calls': '备兑看涨',
  'Loading today’s ideas…': '正在加载今天的机会...',
  'No ideas right now — markets may be closed. Try again in a few minutes.': '当前暂无机会，市场可能已休市。请几分钟后再试。',
  'Sign in to see strike': '登录后查看行权价',
  'Tech read:': '技术面解读：',
  'See all ideas →': '查看全部机会 ->',
  'Real data. Filter by yield, risk, liquidity. Conservative / High-yield / Covered-call presets.': '真实数据。可按收益、风险和流动性过滤，并使用保守、高收益、备兑看涨预设。',
  'Any ticker → live snapshot': '任意代码 -> 实时快照',
  'Option pricing right now': '当前期权定价',
  'Plain-English term-shape label': '通俗的期限结构标签',
  'Run the wheel on the symbol': '在该标的上运行轮转策略',
  'Top cash-secured put + top covered call': '最佳现金担保看跌 + 最佳备兑看涨',
  'Score, APY, distance from price': '评分、APY、距现价距离',
  'Earnings is coming': '财报将近',
  'Date, session, expected move': '日期、时段、预期波动',
  'IV-crush window when applicable': '适用时显示 IV 压缩窗口',
  'What big funds own': '大基金持有什么',
  'Latest 13F filing summary': '最新 13F 持仓摘要',
  'New, added, trimmed, exited': '新建、增持、减持、清仓',
  'Every signal you need, on one page — chain, IV, earnings, big-fund ownership.': '你需要的信号集中在一页：期权链、IV、财报和大基金持仓。',
  'CONSENSUS · BUY': '共识 · 买入',
  'confidence': '置信度',
  'Income Setup': '收益机会',
  'Insider & Momentum': '内部人与动能',
  'Earnings Cushion': '财报缓冲',
  'Analyze': '分析',
  'Stock Analysis': '个股分析',
  'Stock analysis': '个股分析',
  'Open the scanner or analyze a stock.': '打开扫描器，或分析一只股票。',
  'Run analysis': '开始分析',
  'Income setups, insider & momentum, and earnings cushion read on any symbol — resolved into a verdict with the reasoning shown, not a black box.': '对任意标的解读收益机会、内部人与动能、财报缓冲——汇总成一个附理由的结论，而非黑箱输出。',
  'Scored on fundamentals & valuation, not options-chain data.': '基于基本面与估值评分，非期权链数据。',
  'Common questions': '常见问题',
  'The things people ask before signing up.': '注册前大家常问的问题。',
  'Is this free?': '这是免费的吗？',
  'Do you place trades for me?': '你们会替我交易吗？',
  'No. NavOption is research-only. We never connect to your broker. You see the trade here, you place it yourself.': '不会。NavOption 只做研究工具，不连接你的券商账户。你在这里看交易想法，实际下单由你自己完成。',
  'What stocks do you cover?': '覆盖哪些股票？',
  'US stocks and ETFs with liquid options — the universe that actually supports clean wheel trades. Thinly-traded names are skipped on purpose.': '覆盖有流动性期权的美股和 ETF，也就是适合干净轮转交易的标的池。流动性差的标的会刻意跳过。',
  'What does the stock analysis review?': '个股分析会查看什么？',
  'Is the wheel actually profitable?': '轮转策略真的赚钱吗？',
  'Returns depend on the stock, strike, volatility, assignment, and trade management. The strategy can lag strong rallies and lose money in sharp declines; Learn shows the trade-offs and loss scenarios.': '收益取决于股票、行权价、波动率、被行权情况和交易管理。该策略可能在强劲上涨中跑输，并在大幅下跌中亏损；学习页会说明取舍和亏损情景。',
  'iOS app?': '有 iOS App 吗？',
  'Yes. The iOS app syncs your watchlist + positions with the web. Free on the App Store.': '有。iOS App 会和 Web 同步自选与持仓，可在 App Store 免费下载。',
  'How is the stock analysis different from a single chatbot?': '个股分析和普通聊天机器人有什么不同？',
  'What data sources do you use?': '你们使用哪些数据来源？',
  'Scan and unusual-flow: live options chains from a major broker feed. Stock analysis: SEC filings (insider trades + 13F + earnings), fundamentals, and macro data — cross-checked against market news.': '扫描器和异动流：来自主要券商数据源的实时期权链。个股分析：SEC 备案文件（内部人交易、13F 和财报）、基本面与宏观数据，并与市场新闻交叉核对。',
  'Before you decide': '决策之前',
  'Foundations': '基础',
  'Selling the puts and calls': '卖出看跌与看涨',
  'Defense and scaling': '防守与扩展',
  'Advanced risks': '高级风险',
  'Taxes and records': '税务与记录',
  'Is this even for you?': '这适合你吗？',
  'The whole strategy in two modules': '用两个模块讲清整个策略',
  'The mechanical core': '核心操作机制',
  'Options in 5 minutes': '5 分钟理解期权',
  'Wheel vs. buy-and-hold — the honest comparison': '轮转策略 vs 买入持有：坦诚比较',
  'What the wheel is, and what it isn’t': '轮转策略是什么，不是什么',
  'What your stock-investor brain gets right, and wrong': '股票投资直觉哪些有用，哪些危险',
  'Your first cash-secured put — a worked example': '第一笔现金担保看跌：完整例子',
  'Expiration day & assignment': '到期日与被指派',
  'Covered calls after assignment': '被指派后的备兑看涨',
  'Rolling and defense': '移仓与防守',
  'Volatility, IV rank, and when premium is worth it': '波动率、IV Rank 与何时值得收权利金',
  'Common wheel traps': '轮转策略常见陷阱',
  'Tax and recordkeeping basics': '税务与记录基础',
  'Remember': '记住',
  'Try this': '试试看',
  'Option contract': '期权合约',
  'Strike': '行权价',
  'Premium': '权利金',
  'Expiration': '到期日',
  'Exercise': '行权',
  'Assignment': '被指派',
  'Put': '看跌',
  'Call': '看涨',
  'Today': '今天',
  'Each day': '每天',
  'Buyer pays you premium': '买方支付权利金给你',
  'Time decay works in your favor': '时间价值衰减对你有利',
  'Expires worthless OR you buy the shares': '到期归零，或你买入股票',
  'Stock price ↑': '股价 ↑',
  'strike': '行权价',
  'P/L': '盈亏',
  'Buying 100 shares — linear up and down': '买入 100 股：盈亏线性上下',
  'Selling a put — capped gain, real downside': '卖出看跌：收益封顶，下行真实',
  'Selling a call — capped gain, opportunity cost above strike': '卖出看涨：收益封顶，行权价上方有机会成本',
  'If AAPL ends at': '如果 AAPL 收在',
  'Strategy A — Buy 100 shares': '策略 A：买入 100 股',
  'Strategy B — Sell $200 put': '策略 B：卖出 200 美元看跌',
  'Honest takeaways': '坦诚结论',
  'Who this is for': '适合谁',
  'Who this is NOT for': '不适合谁',
  'What transfers cleanly': '可以直接沿用的能力',
  'What’s dangerous to bring across': '照搬会危险的直觉',
  'Concrete example': '具体例子',
  'Three things can happen at expiration:': '到期时可能发生三件事：',
  'Wheel-strategy calculators': '轮转策略计算器',
  'Small, focused calculators for premium yield, assignment, cost basis, and capital planning. All inputs are local — nothing is uploaded.': '一组小而专注的计算器，用于计算权利金收益、被指派概率、成本基础和资金规划。所有输入都在本地，不会上传。',
  'WHEEL TOOLS': '轮转工具',
  'all calculators': '全部计算器',
  'Premium Yield': '权利金收益率',
  'CSP Cash Required': 'CSP 所需现金',
  'Assignment Probability': '被指派概率',
  'Cost Basis After Assignment': '被指派后的成本基础',
  'IV Rank': 'IV Rank',
  'Delta → Probability of Profit': 'Delta -> 盈利概率',
  'Theta Decay': 'Theta 衰减',
  'Wheel Capital': '轮转资金',
  'Covered Call Max Profit': '备兑看涨最大收益',
  'Position Size': '仓位大小',
  'Covered Call vs Dividend': '备兑看涨 vs 股息',
  'Wheel Expectancy': '轮转期望值',
  'Open →': '打开 ->',
  'Formula': '公式',
  'Related': '相关',
  'Premium Yield Calculator': '权利金收益率计算器',
  'Yield = premium ÷ strike. Annualized = yield × (365 / DTE).': '收益率 = 权利金 ÷ 行权价。年化 = 收益率 × (365 / DTE)。',
  'Premium ($)': '权利金（美元）',
  'Strike ($)': '行权价（美元）',
  'DTE (days)': 'DTE（天）',
  'Premium yield': '权利金收益率',
  'Annualized': '年化',
  'Linear extrapolation; assumes premium is repeated each cycle.': '线性外推，假设每个周期都能重复收取权利金。',
  'Cash a broker holds to back a cash-secured put.': '券商为现金担保看跌锁定的资金。',
  'Contracts': '合约数',
  'Cash required (collateral)': '所需现金（担保金）',
  'Net cash if assigned': '被指派后的净现金成本',
  'Collateral minus credit collected.': '担保金减去已收权利金。',
  'The absolute value of delta is a standard approximation of expiration assignment probability.': 'Delta 的绝对值通常可近似为到期被指派概率。',
  'Delta (decimal)': 'Delta（小数）',
  'Approx. assignment probability': '约略被指派概率',
  'Rule of thumb. Real prob also depends on IV skew, dividend, early exercise.': '经验法则。真实概率还取决于 IV 偏斜、股息和提前行权。',
  'If a CSP is assigned, your effective cost basis is the strike minus the premium you kept.': '如果 CSP 被指派，你的有效成本基础是行权价减去已保留权利金。',
  'Premium kept ($)': '保留权利金（美元）',
  'Effective cost basis': '有效成本基础',
  'Discount vs strike': '相对行权价折扣',
  'Where current IV sits inside the trailing 1-year range. ≥50 is the conventional \'high enough to sell\' threshold.': '当前 IV 在过去一年区间中的位置。通常 ≥50 可视为“足够高、可考虑卖出”。',
  'Current IV (%)': '当前 IV（%）',
  '1Y low IV (%)': '一年低点 IV（%）',
  '1Y high IV (%)': '一年高点 IV（%）',
  'For a sold option, PoP ≈ 1 − |delta|.': '对卖出的期权，盈利概率 ≈ 1 − |delta|。',
  'Approx. probability of profit': '约略盈利概率',
  'Assumes you sold the option. Buyers invert the result.': '假设你是卖方。买方可反向理解结果。',
  'A linear approximation. Real theta accelerates as expiration nears.': '线性近似。真实 Theta 会在临近到期时加速。',
  'Premium collected ($)': '已收权利金（美元）',
  'Avg daily theta (linear)': '平均每日 Theta（线性）',
  'As % of premium / day': '每日占权利金比例',
  '# Symbols in rotation': '轮转标的数量',
  'Avg strike ($)': '平均行权价（美元）',
  'Contracts each': '每个标的合约数',
  'Cushion (%)': '缓冲比例（%）',
  'Base collateral': '基础担保金',
  'With cushion': '含缓冲资金',
  'Max profit / share': '每股最大收益',
  'Total': '总计',
  'Return on cost basis': '成本基础回报率',
  'Portfolio ($)': '组合金额（美元）',
  'Risk per trade (%)': '单笔风险（%）',
  'Dollar risk budget': '美元风险预算',
  'Suggested contracts': '建议合约数',
  'Collateral allocated': '已分配担保金',
  'Stock price ($)': '股价（美元）',
  'Annual dividend / share ($)': '每股年股息（美元）',
  'CC annualized yield': '备兑看涨年化收益',
  'Dividend yield': '股息收益率',
  'CC ÷ dividend': 'CC ÷ 股息',
  'Win rate (%)': '胜率（%）',
  'Avg win ($)': '平均盈利（美元）',
  'Avg loss ($)': '平均亏损（美元）',
  'Expected value / cycle': '每周期期望值',
  'Win:Loss ratio': '盈亏比',
  'Breakeven win-rate': '盈亏平衡胜率',
  'DISCOVER': '发现',
  'What is paying today?': '今天什么在支付收益？',
  'A focused wheel-trader workflow: see what\'s paying, pressure-test the trade, layer on today\'s market context, then track the position with roll-aware management.': '为轮转交易者设计的聚焦流程：查看当前收益、压力测试交易、叠加今日市场背景，然后用支持移仓的持仓管理一路跟踪。',
  'Top wheel ideas': '精选轮转机会',
  'Open CSP Screener': '打开 CSP 筛选器',
  'Open CC Screener': '打开 CC 筛选器',
  'Wheel calculators →': '轮转计算器 ->',
  'Terms of Service': '服务条款',
  'Privacy Policy': '隐私政策',
  'Support': '支持',
  'Contact support': '联系支持',
  'Account identifiers.': '账户标识。',
  'Authentication and session.': '认证与会话。',
  'Device and app data.': '设备与应用数据。',
  'Subscription and payment data.': '订阅与支付数据。',
  'Notifications.': '通知。',
  'Analyze and research usage.': '分析和研究使用情况。',
  'Market data.': '市场数据。',
  'Your choices and rights.': '你的选择和权利。',
  'Data retention.': '数据保留。',
  'Third-party services.': '第三方服务。',
  'Security.': '安全。',
  'Children.': '儿童。',
  'Changes.': '变更。',
  'Contact.': '联系。',
  'Use of the Service.': '服务使用。',
  'Educational research only.': '仅用于教育研究。',
  'No brokerage or execution.': '不提供券商或交易执行服务。',
  'Accounts.': '账户。',
  'Subscriptions and billing.': '订阅与计费。',
  'Cancellation.': '取消。',
  'Refunds.': '退款。',
  'Acceptable use.': '可接受使用。',
  'Data and availability.': '数据与可用性。',
  'Intellectual property.': '知识产权。',
  'Disclaimers.': '免责声明。',
  'Limitation of liability.': '责任限制。',
  'Termination.': '终止。',
  'Governing law.': '适用法律。',
  'Contact us.': '联系我们。',
  'Opening Stripe Checkout...': '正在打开 Stripe 结账...',
  'Loading workspace...': '正在加载工作区...',
  'Working...': '处理中...',
  'Apply': '应用',
  'Cancel': '取消',
  'Close': '关闭',
  'Delete': '删除',
  'Edit': '编辑',
  'Save': '保存',
  'Search': '搜索',
  'Loading': '正在加载',
  'Retry': '重试',
  'Refresh': '刷新',
  'Reset': '重置',
  'All': '全部',
  'Any': '任意',
  'Score': '评分',
  'Risk': '风险',
  'Date': '日期',
  'Side': '方向',
  'Expiry': '到期日',
  'Underlying': '标的',
  'Volume': '成交量',
  'Open interest': '未平仓量',
  'Change': '涨跌',
  'Sector': '板块',
  'News': '新闻',
  'Watchlist': '自选',
  'Positions': '持仓',
  'Pricing': '定价',
  'Current plan': '当前套餐',
  'Best value': '最佳价值',
  'Current free plan': '当前免费套餐',
  'Manage subscription': '管理订阅',
  'Sign out': '退出登录',
  'See the top cash-secured puts and covered calls across the option universe we track — filtered by listed annualized premium, risk, and days to expiration.': '查看我们覆盖的期权标的池里最好的现金担保看跌与备兑看涨，可按挂牌年化权利金、风险和剩余天数筛选。',
  'How the two paths differ': '两条路径的差别在哪',
  'Shares alone vs. shares with a call sold.': '只持股，和持股再卖一张 call。',
  'Holding the shares owns the whole move, up or down. Selling a call against them gives up part of a rally in exchange for the option premium you collect along the way, and if you end up owning the shares, it keeps selling calls on them.': '只持股，涨跌全都由你承担。对这些股票卖一张 call，是把一部分上涨让出去，换成一路收到的期权权利金；而一旦你真的拿到了股票，这条路就继续对它们卖 call。',
  'Stock price at expiry →': '到期时的股价 →',
  'Profit / loss at expiry': '到期时的盈亏',
  'Covered call': '备兑看涨',
  'Shares': '只持股',
  'Stock analysis runs general-purpose models with explicit prompts. We don’t fine-tune on user behavior and we don’t build a profile of you.': '个股分析用的是通用模型加明确的提示词。我们不拿用户行为去微调模型，也不给你建档。',
}

// What the walker found, and what it wrote in place of it.
//
// Both halves matter. Restoring compares the node's current value against
// `translated`, so the walker only ever undoes its own edit. Anything React
// has rendered since — which is every t() string, and React already localizes
// those itself — no longer matches and is left alone. Without that check the
// walker recorded React's Chinese as the "original" and then overwrote React's
// English with it on the way back, which made the switch one-way.
type Rewrite = { original: string; translated: string }

const textOriginal = new WeakMap<Text, Rewrite>()
const attrOriginal = new WeakMap<Element, Map<string, Rewrite>>()
const translatableAttrs = ['aria-label', 'alt', 'placeholder', 'title'] as const

function normalized(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

function shouldSkipText(element: Element | null): boolean {
  return !!element?.closest('[data-i18n-skip="true"], .analyze, code, pre, script, style, svg, input, textarea')
}

function shouldSkipAttrs(element: Element | null): boolean {
  return !!element?.closest('[data-i18n-skip="true"], .analyze, script, style, svg')
}

function translateStatic(raw: string): string {
  const leading = raw.match(/^\s*/)?.[0] || ''
  const trailing = raw.match(/\s*$/)?.[0] || ''
  let body = normalized(raw)
  if (!body || !/[A-Za-z]/.test(body)) return raw
  if (staticZh[body]) return `${leading}${staticZh[body]}${trailing}`

  const replacements = Object.entries(staticZh)
    .filter(([key]) => key.length >= 4 && body.includes(key))
    .sort((a, b) => b[0].length - a[0].length)
  for (const [key, value] of replacements) body = body.split(key).join(value)
  return `${leading}${body}${trailing}`
}

function translateTextNode(node: Text, language: Language) {
  const parent = node.parentElement
  if (shouldSkipText(parent)) return
  const current = node.nodeValue || ''
  const seen = textOriginal.get(node)

  if (language === 'en') {
    if (seen && current === seen.translated) node.nodeValue = seen.original
    return
  }
  if (!current.trim()) return
  if (seen && current === seen.translated) return
  const translated = translateStatic(current)
  if (translated === current) return
  textOriginal.set(node, { original: current, translated })
  node.nodeValue = translated
}

function translateElementAttrs(element: Element, language: Language) {
  if (shouldSkipAttrs(element)) return
  let originals = attrOriginal.get(element)
  for (const attr of translatableAttrs) {
    const current = element.getAttribute(attr)
    if (!current) continue
    const seen = originals?.get(attr)

    if (language === 'en') {
      if (seen && current === seen.translated) element.setAttribute(attr, seen.original)
      continue
    }
    if (seen && current === seen.translated) continue
    const translated = translateStatic(current)
    if (translated === current) continue
    if (!originals) {
      originals = new Map()
      attrOriginal.set(element, originals)
    }
    originals.set(attr, { original: current, translated })
    element.setAttribute(attr, translated)
  }
}

function translateTree(root: ParentNode, language: Language) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let textNode = walker.nextNode() as Text | null
  while (textNode) {
    translateTextNode(textNode, language)
    textNode = walker.nextNode() as Text | null
  }
  if (root instanceof Element) translateElementAttrs(root, language)
  for (const element of Array.from(root.querySelectorAll('*'))) translateElementAttrs(element, language)
}

export type MessageKey = keyof typeof messages.en

type I18nContextValue = {
  language: Language
  setLanguage: (language: Language) => void
  t: (key: MessageKey) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

function detectLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'en' || stored === 'zh') return stored
  } catch { /* ignore unavailable storage */ }
  const browserLang = navigator.language || navigator.languages?.[0] || 'en'
  return browserLang.toLowerCase().startsWith('zh') ? 'zh' : 'en'
}

// Module-level mirror of the active language, for callers that are not React
// components. The server now renders insights, gates and the daily report in
// one language, so `api()` — a plain function — has to know which one to ask
// for, and it cannot read context.
let activeLanguage: Language = detectLanguage()

export function getLanguage(): Language {
  return activeLanguage
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => detectLanguage())

  const setLanguage = (next: Language) => {
    activeLanguage = next
    setLanguageState(next)
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore unavailable storage */ }
    onLanguageChange?.(next)
  }

  useEffect(() => {
    activeLanguage = language
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : 'en'
    window.setTimeout(() => translateTree(document.body, language), 0)
  }, [language])

  useEffect(() => {
    const run = () => translateTree(document.body, language)
    run()
    const observer = new MutationObserver((mutations) => {
      window.setTimeout(() => {
        for (const mutation of mutations) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node instanceof Text) translateTextNode(node, language)
            if (node instanceof Element) translateTree(node, language)
          }
          if (mutation.type === 'characterData' && mutation.target instanceof Text) {
            translateTextNode(mutation.target, language)
          }
          if (mutation.type === 'attributes' && mutation.target instanceof Element) {
            translateElementAttrs(mutation.target, language)
          }
        }
      }, 0)
    })
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: [...translatableAttrs] })
    return () => observer.disconnect()
  }, [language])

  const value = useMemo<I18nContextValue>(() => ({
    language,
    setLanguage,
    t: (key) => messages[language][key] || messages.en[key],
  }), [language])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

// onLanguageChange lets the app persist the choice to the account, so
// server-initiated push — which has no request to read a language from —
// arrives in the language the user picked here. Set once at startup.
let onLanguageChange: ((next: Language) => void) | null = null

export function setLanguageChangeHandler(fn: (next: Language) => void) {
  onLanguageChange = fn
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used inside I18nProvider')
  return value
}
